import React, { useState, useEffect, useRef, useCallback, useMemo, Suspense } from 'react';
import { chemistryApi } from '../../api/chemistry';
import { ComposableMap, Geographies, Geography } from 'react-simple-maps';
// PinnedStructureCard no longer used — multi-pin cards rendered inline
import { usePatentMapStore } from '../../stores/patentMapStore';
import StructureImage from './StructureImage';
import ExternalLinkWarning, { openExternalLink } from '../common/ExternalLinkWarning';
import CompoundDetailModal from './CompoundDetailModal';
import PatentDetailModal from './PatentDetailModal';

// Lazy-load 3D Globe — the Three.js bundle is large and competes for GPU memory
// with AG Grid, Plotly, and Ketcher. Only load when the user clicks the 3D tab.
const PatentCollisionMap3D = React.lazy(() => import('./PatentCollisionMap3D'));

// Premium CSS animations - makes it feel ALIVE
const premiumStyles = `
/* Verdict icon reveal with bounce */
@keyframes iconReveal {
  0% { transform: scale(0) rotate(-10deg); opacity: 0; }
  50% { transform: scale(1.3) rotate(5deg); opacity: 1; }
  70% { transform: scale(0.9) rotate(-2deg); }
  100% { transform: scale(1) rotate(0deg); opacity: 1; }
}

/* Particle burst effect */
@keyframes particleBurst {
  0% { transform: scale(0); opacity: 0.8; }
  100% { transform: scale(2.5); opacity: 0; }
}

/* Subtle continuous pulse */
@keyframes subtlePulse {
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.02); opacity: 0.95; }
}

/* Jurisdiction map pulsing dots */
@keyframes jurisdictionPulse {
  0%, 100% { opacity: 0.8; transform: scale(1); }
  50% { opacity: 1; transform: scale(1.2); }
}

/* Progress bar fill with glow */
@keyframes progressFill {
  from { width: 0; }
}

@keyframes glowSweep {
  0% { left: -20%; opacity: 0; }
  50% { opacity: 1; }
  100% { left: 120%; opacity: 0; }
}

/* Floating animation for molecules */
@keyframes float {
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-3px); }
}

/* Gradient shift for backgrounds */
@keyframes gradientShift {
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
}

/* Glow pulse for selected items */
@keyframes glowPulse {
  0%, 100% { box-shadow: 0 0 20px currentColor; filter: brightness(1); }
  50% { box-shadow: 0 0 40px currentColor; filter: brightness(1.1); }
}

/* Staggered fade in */
@keyframes fadeSlideIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

/* View transition */
@keyframes viewEnter {
  from { opacity: 0; transform: scale(0.98); }
  to { opacity: 1; transform: scale(1); }
}

@keyframes viewExit {
  from { opacity: 1; transform: scale(1); }
  to { opacity: 0; transform: scale(0.98); }
}

/* Classes */
.verdict-icon {
  animation: iconReveal 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
  position: relative;
}

.verdict-icon::before {
  content: '';
  position: absolute;
  inset: -20px;
  border-radius: 50%;
  background: radial-gradient(circle, currentColor 0%, transparent 70%);
  animation: particleBurst 0.8s ease-out forwards;
  pointer-events: none;
  opacity: 0.3;
}

.verdict-badge {
  background-size: 200% 200%;
  animation: gradientShift 8s ease infinite, subtlePulse 3s ease-in-out infinite;
}

.progress-animated {
  animation: progressFill 0.8s cubic-bezier(0.4, 0, 0.2, 1) forwards;
  position: relative;
  overflow: hidden;
}

.progress-animated::after {
  content: '';
  position: absolute;
  top: 0;
  left: -20%;
  width: 20%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent);
  animation: glowSweep 1.5s ease-in-out 0.5s;
}

.stat-card {
  animation: fadeSlideIn 0.5s ease-out backwards;
}

.stat-card:nth-child(1) { animation-delay: 0.1s; }
.stat-card:nth-child(2) { animation-delay: 0.2s; }
.stat-card:nth-child(3) { animation-delay: 0.3s; }
.stat-card:nth-child(4) { animation-delay: 0.4s; }

.zone-bar {
  animation: fadeSlideIn 0.4s ease-out backwards;
}

.zone-bar:nth-child(1) { animation-delay: 0.2s; }
.zone-bar:nth-child(2) { animation-delay: 0.3s; }
.zone-bar:nth-child(3) { animation-delay: 0.4s; }
.zone-bar:nth-child(4) { animation-delay: 0.5s; }
.zone-bar:nth-child(5) { animation-delay: 0.6s; }

.view-content {
  animation: viewEnter 0.3s ease-out;
}

.molecule-float {
  animation: float 4s ease-in-out infinite;
}

.glow-pulse {
  animation: glowPulse 2s ease-in-out infinite;
}

/* Map styles */
.map-country {
  transition: fill 0.2s ease, stroke 0.2s ease;
  cursor: pointer;
}

.map-country:hover {
  filter: brightness(1.2);
}
`;

// Inject styles once
if (typeof document !== 'undefined' && !document.getElementById('premium-styles')) {
  const styleEl = document.createElement('style');
  styleEl.id = 'premium-styles';
  styleEl.textContent = premiumStyles;
  document.head.appendChild(styleEl);
}

// World map TopoJSON — served locally (no CDN dependency)
const GEO_URL = '/countries-110m.json';

// Mapping from patent jurisdiction codes to country names (for map display)
const JURISDICTION_TO_COUNTRY: Record<string, string> = {
  'US': 'United States of America',
  'CN': 'China',
  'EP': 'Europe',  // European Patent Office - highlights multiple countries
  'WO': 'International',  // WIPO/PCT - global
  'JP': 'Japan',
  'KR': 'South Korea',
  'AU': 'Australia',
  'CA': 'Canada',
  'GB': 'United Kingdom',
  'DE': 'Germany',
  'FR': 'France',
  'IN': 'India',
  'BR': 'Brazil',
  'RU': 'Russia',
  'MX': 'Mexico',
  'TW': 'Taiwan',
  'IL': 'Israel',
  'SG': 'Singapore',
  'NZ': 'New Zealand',
  'ZA': 'South Africa',
};

// Reverse mapping: country name to jurisdiction code
const COUNTRY_TO_JURISDICTION: Record<string, string[]> = {
  'United States of America': ['US', 'USPTO'],
  'China': ['CN', 'CNIPA'],
  'Japan': ['JP', 'JPO'],
  'South Korea': ['KR', 'KIPO'],
  'Australia': ['AU'],
  'Canada': ['CA'],
  'United Kingdom': ['GB', 'EP', 'EPO'],
  'Germany': ['DE', 'EP', 'EPO'],
  'France': ['FR', 'EP', 'EPO'],
  'India': ['IN'],
  'Brazil': ['BR'],
  'Russia': ['RU'],
  'Mexico': ['MX'],
  'Taiwan': ['TW'],
  'Israel': ['IL'],
  'Singapore': ['SG'],
  'New Zealand': ['NZ'],
  'South Africa': ['ZA'],
  'Italy': ['EP'],
  'Spain': ['EP'],
  'Netherlands': ['EP'],
  'Belgium': ['EP'],
  'Switzerland': ['EP'],
  'Austria': ['EP'],
  'Sweden': ['EP'],
  'Poland': ['EP'],
  'Denmark': ['EP'],
  'Finland': ['EP'],
  'Ireland': ['EP'],
  'Norway': ['EP'],
  'Portugal': ['EP'],
  'Czechia': ['EP'],
  'Hungary': ['EP'],
  'Greece': ['EP'],
  'Romania': ['EP'],
  'Bulgaria': ['EP'],
  'Croatia': ['EP'],
  'Luxembourg': ['EP'],
};

// Full names for jurisdiction codes
const JURISDICTION_NAMES: Record<string, string> = {
  'CN': 'China',
  'US': 'United States',
  'EP': 'Europe (EPO)',
  'WO': 'International (WIPO)',
  'JP': 'Japan',
  'KR': 'South Korea',
  'AU': 'Australia',
  'CA': 'Canada',
  'GB': 'United Kingdom',
  'DE': 'Germany',
  'FR': 'France',
  'IN': 'India',
  'BR': 'Brazil',
  // Patent office codes (from compound_patents.office)
  'USPTO': 'United States (USPTO)',
  'EPO': 'Europe (EPO)',
  'JPO': 'Japan (JPO)',
  'CNIPA': 'China (CNIPA)',
  'KIPO': 'South Korea (KIPO)',
  'WIPO': 'International (WIPO)',
};

// Patent office SVG pixel positions (relative to 800x400 viewBox)
const OFFICE_LOCATIONS: Record<string, { cx: number; cy: number; name: string }> = {
  'USPTO': { cx: 150, cy: 160, name: 'USPTO (United States)' },
  'EPO':   { cx: 350, cy: 120, name: 'EPO (Europe)' },
  'JPO':   { cx: 580, cy: 150, name: 'JPO (Japan)' },
  'CNIPA': { cx: 540, cy: 170, name: 'CNIPA (China)' },
  'KIPO':  { cx: 560, cy: 150, name: 'KIPO (South Korea)' },
  'WIPO':  { cx: 370, cy: 130, name: 'WIPO (International)' },
};

// Get color based on patent count - darker = more patents
const getJurisdictionColorFromCount = (count: number, maxCount: number): string => {
  if (count === 0) return '#1e293b'; // Dark slate - no patents

  // Normalize count to 0-1 range (log scale for better distribution)
  const normalizedCount = Math.log(count + 1) / Math.log(maxCount + 1);

  // Interpolate from light red to dark red based on count
  // More patents = darker/more intense red
  if (normalizedCount > 0.7) return '#991b1b'; // red-800
  if (normalizedCount > 0.4) return '#dc2626'; // red-600
  if (normalizedCount > 0.2) return '#ef4444'; // red-500
  return '#f87171'; // red-400
};

// Count-up animation component for numbers
const CountUp: React.FC<{ end: number; duration?: number; suffix?: string; decimals?: number }> = ({
  end,
  duration = 800,
  suffix = '',
  decimals = 0,
}) => {
  const [value, setValue] = useState(0);
  const startTime = useRef<number | null>(null);

  useEffect(() => {
    startTime.current = null;
    const animate = (timestamp: number) => {
      if (!startTime.current) startTime.current = timestamp;
      const progress = Math.min((timestamp - startTime.current) / duration, 1);
      // Ease-out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(eased * end);
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    requestAnimationFrame(animate);
  }, [end, duration]);

  return <>{decimals > 0 ? value.toFixed(decimals) : Math.round(value).toLocaleString()}{suffix}</>;
};

// Zone configuration - matches backend CollisionZone enum (isostere-aware system)
const ZONE_CONFIG: Record<number, { color: string; label: string; description: string; risk: string }> = {
  1: { color: '#dc2626', label: 'Literal Match', description: 'Exact InChI match to enumerated molecule', risk: 'Definite infringement' },
  2: { color: '#f97316', label: 'Chemical Isostere', description: 'F↔Cl, CH₃↔CF₃, halogen exchange', risk: 'Likely infringement' },
  3: { color: '#eab308', label: 'Biological Isostere', description: 'COOH↔tetrazole, phenyl↔pyridyl', risk: 'Possible infringement' },
  4: { color: '#facc15', label: 'Close Analog', description: 'High MCS similarity to patent scope', risk: 'Obviousness risk' },
  5: { color: '#22c55e', label: 'Novel', description: 'Outside patent scope', risk: 'Freedom to operate' },
};

interface PatentCollisionMapProps {
  fileId: string | number;
  projectId?: number;
  onMoleculeSelect?: (molecule: any) => void;
  fullPage?: boolean;
}

interface PatentMatch {
  patent_number?: string;
  patent_title?: string;
  match_type?: 'exact' | 'similar';
  similarity: number | null;
  surechembl_id?: string;
  matched_smiles?: string;
}

type SimilarityViewMode = 'tanimoto' | 'mcs' | 'substructure';

interface McsResult {
  mcs_atoms: number;
  mcs_fraction: number;
  transform_distance: number;
  mcs_smarts: string;
  mol1_atoms: number;
  mol2_atoms: number;
  error?: string;
}

interface SubstructureResult {
  is_match: boolean;
  query_atoms: number;
  target_atoms: number;
  matching_atoms: number;
  direction?: string | null;
  error?: string;
}

interface PatentSummary {
  patent_number: string;
  patent_title: string;
  match_count: number;
  exact_count: number;
  similar_count: number;
}

interface MoleculeData {
  id: number;
  smiles: string;
  name?: string;
  inchi?: string;
  ip_status?: string | null;
  patent_count_exact?: number;
  patent_count_similar?: number;
  max_similarity?: number | null;
  closest_patent?: string | null;
  enriched_at?: string | null;
  zone: number;
  patent_matches?: PatentMatch[];
  top_patent_matches?: PatentMatch[]; // New API format
  // Isostere detection fields (from backend CollisionResult)
  isostere_type?: 'chemical' | 'biological' | null;
  isostere_name?: string | null;
  transform_smirks?: string | null;
  matched_smiles?: string | null;
  mcs_atoms?: number | null;
  mcs_fraction?: number | null;
}

interface MapData {
  file_id: number;
  patents: PatentSummary[];
  molecules: MoleculeData[];
  summary: {
    total_molecules: number;
    enriched_molecules: number;
    pending_molecules: number;
    zone_counts: Record<string, number>;
    total_patents: number;
  };
  zone_colors: Record<number, string>;
  zone_labels: Record<number, string>;
  jurisdictions?: Record<string, number>;
}

interface Position {
  x: number;
  y: number;
  radius?: number;
  zone?: number;
}

// Shared molecule patent cache — populated when collision map loads, read by PatentsTab
// Key shape: "${fileId}:${smiles}" — prevents cross-file stale data
export interface CachedMoleculePatent {
  zone: number;
  patent_count_exact: number;
  patent_count_similar: number;
  max_similarity: number | null;
}
export const moleculePatentCache = new Map<string, CachedMoleculePatent>();
export let currentCollisionMapFileId: string | number | null = null;

// Loading indicator with elapsed time
const LoadingIndicator: React.FC<{ fullPage?: boolean }> = ({ fullPage }) => {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => setElapsed(prev => prev + 1), 1000);
    return () => clearInterval(interval);
  }, []);
  const formatTime = (s: number) => s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${s % 60}s`;
  return (
    <div className={`flex flex-col items-center justify-center h-full min-h-[400px] ${fullPage ? '' : 'rounded-xl'}`} style={{ backgroundColor: 'var(--content-bg)' }}>
      <div className="w-10 h-10 border-3 border-t-blue-500 rounded-full animate-spin" style={{ borderColor: 'var(--card-border)', borderTopColor: '#3b82f6' }} />
      <div className="mt-6 text-center">
        <div className="font-medium" style={{ color: 'var(--text-primary)' }}>Loading patent collision data...</div>
        <div className="text-sm mt-2" style={{ color: 'var(--text-secondary)' }}>Analyzing molecules against 28.4M patent compounds</div>
        {/* Animated progress bar */}
        <div className="mt-4 w-48 mx-auto h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--card-border)' }}>
          <div className="h-full rounded-full" style={{
            background: 'linear-gradient(90deg, #3b82f6, #14b8a6)',
            animation: 'progressIndeterminate 1.5s ease-in-out infinite',
            width: '40%',
          }} />
        </div>
        <div className="text-xs mt-2" style={{ color: 'var(--text-muted)' }}>
          Elapsed: {formatTime(elapsed)} {elapsed > 10 && '— Large files may take longer'}
        </div>
      </div>
      <style>{`
        @keyframes progressIndeterminate {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(350%); }
        }
      `}</style>
    </div>
  );
};

// Simple hash function for consistent positioning
const hashString = (str: string): number => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return Math.abs(hash);
};

// Seeded random for consistent scatter
const seededRandom = (seed: number): number => {
  const x = Math.sin(seed * 9999) * 10000;
  return x - Math.floor(x);
};

// Easing function for smooth animation
const easeInOutCubic = (t: number): number => {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
};

const PatentCollisionMap: React.FC<PatentCollisionMapProps> = ({ fileId, projectId, onMoleculeSelect, fullPage = false }) => {
  const setPendingScrollMoleculeId = usePatentMapStore(s => s.setPendingScrollMoleculeId);
  const [mapData, setMapData] = useState<MapData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Animation state - simplified phases
  const [animationPhase, setAnimationPhase] = useState<'loading' | 'analyzing' | 'molecules' | 'circles' | 'lines' | 'complete'>('loading');
  const animationStartRef = useRef<number>(0);
  const animationFrameRef = useRef<number | null>(null);

  // Selection state
  const [selectedMolecule, setSelectedMolecule] = useState<MoleculeData | null>(null);
  const [selectedPatent, setSelectedPatent] = useState<PatentSummary | null>(null);

  // External link warning state
  const [extWarning, setExtWarning] = useState({ url: '', isOpen: false });
  const [compoundDetail, setCompoundDetail] = useState<{ id: string; smiles?: string; similarity?: number; zone?: number; patent?: string } | null>(null);

  // Pinned molecules — shared across 2D Scatter and 3D Globe views
  const [pinnedMolecules, setPinnedMolecules] = useState<Map<number, MoleculeData>>(new Map());
  const [hoveredItem, setHoveredItem] = useState<{ type: string; id: number; data: any } | null>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Toggle a molecule in the pinned set
  const togglePin = useCallback((mol: MoleculeData) => {
    setPinnedMolecules(prev => {
      const next = new Map(prev);
      if (next.has(mol.id)) {
        next.delete(mol.id);
      } else {
        next.set(mol.id, mol);
      }
      return next;
    });
  }, []);

  // Auto-expand sidebar when pins or selection change
  useEffect(() => {
    if (pinnedMolecules.size > 0 || selectedPatent) {
      setSidebarCollapsed(false);
      setLegendCollapsed(true);
    } else {
      setLegendCollapsed(false);
    }
  }, [pinnedMolecules.size, selectedPatent]);

  // Threshold filters
  const [showInfoTooltip, setShowInfoTooltip] = useState(false);
  const [visibleZones, setVisibleZones] = useState<number[]>([1, 2, 3, 4, 5]);

  // Zone legend collapse — collapsed when molecule is selected, expanded otherwise
  const [legendCollapsed, setLegendCollapsed] = useState(false);

  // Display controls for large files
  const [displayLimit, setDisplayLimit] = useState(100);

  // View mode types: scatter (2D), globe (3D), overlap (Venn), jurisdictions
  type ViewMode = 'scatter' | 'globe' | 'overlap' | 'jurisdictions';
  const [viewMode, setViewMode] = useState<ViewMode>('scatter');

  // Legacy alias — keep explorerSubMode wired so canvas code still works
  const explorerSubMode = (viewMode === 'globe' ? '3d' : 'scatter') as 'scatter' | '3d' | 'heatmap';
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Jurisdictions sub-mode (list vs map)
  const [jurisdictionsSubMode, setJurisdictionsSubMode] = useState<'list' | 'map'>('list');

  // Export dropdown
  const [exportOpen, setExportOpen] = useState(false);

  // At-risk-only toggle
  const [atRiskOnly, setAtRiskOnly] = useState(false);

  // (Similarity mode removed — was only used in expert view)

  // (World map hover state removed)

  // Zoom controls for Explorer view
  const [zoomLevel, setZoomLevel] = useState(1);
  const [panOffsetX, setPanOffsetX] = useState(0);
  const [panOffsetY, setPanOffsetY] = useState(0);
  const [isPanning, setIsPanning] = useState(false);
  const panStartRef = useRef<{ x: number; y: number; originX: number; originY: number } | null>(null);
  const didDragRef = useRef(false);

  // Reactive dark mode — triggers canvas redraw on theme toggle
  const [isDarkMode, setIsDarkMode] = useState(() => document.body.classList.contains('theme-dark'));
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDarkMode(document.body.classList.contains('theme-dark'));
    });
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  const handleZoomIn = () => setZoomLevel(prev => Math.min(prev * 1.2, 3));
  const handleZoomOut = () => setZoomLevel(prev => Math.max(prev / 1.2, 0.5));
  const handleZoomReset = () => { setZoomLevel(1); setPanOffsetX(0); setPanOffsetY(0); };

  // Collision view mode: "my-molecules" shows your molecules as focal points with patent context,
  // "patent-landscape" shows patent families as focal points with your molecules as markers
  const [collisionViewMode, setCollisionViewMode] = useState<'my-molecules' | 'patent-landscape'>('my-molecules');

  // Three-way similarity toggle for Risk Report patent hits table
  const [similarityViewMode, setSimilarityViewMode] = useState<SimilarityViewMode>('tanimoto');
  const [mcsCache, setMcsCache] = useState<Map<string, McsResult>>(new Map());
  const [substructureCache, setSubstructureCache] = useState<Map<string, SubstructureResult>>(new Map());
  const [similarityLoading, setSimilarityLoading] = useState(false);

  // BroadcastChannel for patent map popout sync
  const patentMapChannelRef = useRef<BroadcastChannel | null>(null);

  useEffect(() => {
    const ch = new BroadcastChannel('patent-map');
    patentMapChannelRef.current = ch;

    ch.onmessage = (e) => {
      if (e.data?.type === 'popout-ready' && mapData) {
        ch.postMessage({ type: 'init', mapData, selectedMolecule, visibleZones });
      }
      if (e.data?.type === 'select-molecule') {
        setSelectedMolecule(e.data.molecule);
        if (e.data.molecule) {
          onMoleculeSelect?.(e.data.molecule);
        }
      }
    };

    return () => ch.close();
  }, [mapData, selectedMolecule, visibleZones, onMoleculeSelect]);

  const handlePopoutPatentMap = useCallback(() => {
    const w = window.open(
      `/popout/patent-map/${fileId}`,
      'patent-map',
      'width=1200,height=800,menubar=no,toolbar=no,location=no,status=no,scrollbars=no,resizable=yes'
    );
    if (w) w.focus();
  }, [fileId]);

  // Clear similarity caches when file changes
  useEffect(() => {
    setMcsCache(new Map());
    setSubstructureCache(new Map());
    setSimilarityViewMode('tanimoto');
  }, [fileId]);

  // Fetch MCS or substructure data for patent hits
  const fetchSimilarityData = useCallback(async (mode: SimilarityViewMode) => {
    if (mode === 'tanimoto' || !mapData?.molecules?.length) return;

    // Build uncached pairs
    const existingKeys = mode === 'mcs' ? mcsCache : substructureCache;
    const pairs: { mol_smiles: string; surechembl_id: string; key: string }[] = [];
    mapData.molecules.forEach(mol => {
      const matches = mol.top_patent_matches || mol.patent_matches || [];
      matches.forEach(pm => {
        const key = `${mol.smiles}||${pm.surechembl_id || pm.patent_number}`;
        if (!existingKeys.has(key)) {
          pairs.push({
            mol_smiles: mol.smiles,
            surechembl_id: pm.surechembl_id || pm.patent_number || '',
            key,
          });
        }
      });
    });

    if (pairs.length === 0) return;
    setSimilarityLoading(true);
    try {
      const resp = await chemistryApi.batchSimilarityCompare(
        pairs.map(p => ({ mol_smiles: p.mol_smiles, patent_smiles: null, surechembl_id: p.surechembl_id })),
        mode as 'mcs' | 'substructure'
      );
      if (mode === 'mcs') {
        const newCache = new Map(mcsCache);
        resp.results.forEach((r: any, i: number) => {
          if (pairs[i]) newCache.set(pairs[i].key, r);
        });
        setMcsCache(newCache);
      } else {
        const newCache = new Map(substructureCache);
        resp.results.forEach((r: any, i: number) => {
          if (pairs[i]) newCache.set(pairs[i].key, r);
        });
        setSubstructureCache(newCache);
      }
    } catch (err) {
      console.error(`Failed to fetch ${mode} data:`, err);
    } finally {
      setSimilarityLoading(false);
    }
  }, [mapData, mcsCache, substructureCache]);

  // Auto-select view based on molecule count
  const getDefaultView = useCallback((_moleculeCount: number): ViewMode => {
    const savedPref = localStorage.getItem('collisionMapViewPreference');
    if (savedPref && ['scatter', 'globe', 'overlap', 'jurisdictions'].includes(savedPref)) {
      return savedPref as ViewMode;
    }
    return 'scatter';
  }, []);

  // Save view preference to localStorage
  const handleViewChange = useCallback((newView: ViewMode) => {
    setIsTransitioning(true);
    setTimeout(() => {
      setViewMode(newView);
      localStorage.setItem('collisionMapViewPreference', newView);
      setTimeout(() => setIsTransitioning(false), 300);
    }, 200);
  }, []);

  // Verdict calculation for single molecule or portfolio
  const calculateVerdict = useCallback((mols: MoleculeData[]): 'CLEAR' | 'REVIEW' | 'BLOCKED' => {
    const total = mols.length;
    if (total === 0) return 'CLEAR';

    const zone1Count = mols.filter(m => m.zone === 1).length;
    const zone2Count = mols.filter(m => m.zone === 2).length;
    const zone3Count = mols.filter(m => m.zone === 3).length;
    const zone4Count = mols.filter(m => m.zone === 4).length;

    const zone1Percent = (zone1Count / total) * 100;
    const zone34Percent = ((zone3Count + zone4Count) / total) * 100;

    // BLOCKED: >10% in Zone 1
    if (zone1Percent > 10) return 'BLOCKED';

    // REVIEW: Any in Zones 1-2, or >5% in Zones 3-4
    if (zone1Count > 0 || zone2Count > 0 || zone34Percent > 5) return 'REVIEW';

    // CLEAR: 0 in Zones 1-2, <5% in Zones 3-4
    return 'CLEAR';
  }, []);

  // Export to CSV
  const handleExportCSV = () => {
    if (!mapData?.molecules?.length) {
      alert('No data to export');
      return;
    }

    const molecules = mapData.molecules;

    // CSV header
    const headers = [
      'ID',
      'Name',
      'SMILES',
      'Zone',
      'Zone Label',
      'Risk Level',
      'Max Similarity',
      'Closest Patent',
      'Exact Matches',
      'Similar Matches',
      'Isostere Type',
      'MCS Fraction',
      'Top Patent Matches'
    ];

    // CSV rows
    const rows = molecules.map(mol => {
      const zoneConfig = ZONE_CONFIG[mol.zone] || {};
      const matches = mol.top_patent_matches || mol.patent_matches || [];
      const topMatches = matches.slice(0, 3).map(m =>
        `${m.surechembl_id || m.patent_number || 'Unknown'}(${((m.similarity || 0) * 100).toFixed(1)}%)`
      ).join('; ');

      return [
        mol.id,
        mol.name || '',
        mol.smiles || '',
        mol.zone,
        zoneConfig.label || '',
        zoneConfig.risk || '',
        mol.max_similarity ? `${(mol.max_similarity * 100).toFixed(1)}%` : '',
        mol.closest_patent || '',
        mol.patent_count_exact || 0,
        mol.patent_count_similar || 0,
        mol.isostere_type || '',
        mol.mcs_fraction ? `${(mol.mcs_fraction * 100).toFixed(1)}%` : '',
        topMatches
      ];
    });

    // Build CSV content
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => {
        // Escape quotes and wrap in quotes if contains comma or quote
        const str = String(cell);
        if (str.includes(',') || str.includes('"') || str.includes('\n')) {
          return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
      }).join(','))
    ].join('\n');

    // Download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `patent-collision-report-${mapData.file_id}-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  // Export to PDF (opens print dialog with formatted report)
  const handleExportPDF = () => {
    if (!mapData?.molecules?.length) {
      alert('No data to export');
      return;
    }

    const molecules = mapData.molecules;
    const zoneCounts = mapData.summary?.zone_counts || {};
    const totalMols = molecules.length;

    // Create a new window with formatted content
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Please allow popups to generate PDF report');
      return;
    }

    const atRiskCount = (zoneCounts['1'] || 0) + (zoneCounts['2'] || 0) + (zoneCounts['3'] || 0) + (zoneCounts['4'] || 0);
    const novelCount = zoneCounts['5'] || 0;
    const atRiskPercent = totalMols > 0 ? ((atRiskCount / totalMols) * 100).toFixed(1) : '0';

    // Build HTML report
    const htmlContent = `
<!DOCTYPE html>
<html>
<head>
  <title>Patent Landscape Report - File ${mapData.file_id}</title>
  <style>
    body { font-family: Arial, sans-serif; margin: 40px; color: #333; }
    h1 { color: #0f172a; border-bottom: 2px solid #0d9488; padding-bottom: 10px; }
    h2 { color: #1e293b; margin-top: 30px; }
    .summary { background: #f8fafc; padding: 20px; border-radius: 8px; margin: 20px 0; }
    .summary-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 20px; }
    .stat-box { text-align: center; padding: 15px; background: white; border-radius: 8px; box-shadow: 0 1px 3px rgba(0,0,0,0.1); }
    .stat-value { font-size: 28px; font-weight: bold; }
    .stat-label { color: #64748b; font-size: 12px; text-transform: uppercase; }
    .zone-table { width: 100%; border-collapse: collapse; margin: 20px 0; }
    .zone-table th, .zone-table td { padding: 12px; text-align: left; border-bottom: 1px solid #e2e8f0; }
    .zone-table th { background: #f1f5f9; font-weight: 600; }
    .zone-dot { display: inline-block; width: 12px; height: 12px; border-radius: 50%; margin-right: 8px; }
    .molecule-table { width: 100%; border-collapse: collapse; font-size: 11px; }
    .molecule-table th, .molecule-table td { padding: 8px; text-align: left; border-bottom: 1px solid #e2e8f0; }
    .molecule-table th { background: #f1f5f9; }
    .at-risk { color: #dc2626; }
    .novel { color: #16a34a; }
    .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #e2e8f0; color: #64748b; font-size: 12px; }
    @media print {
      body { margin: 20px; }
      .no-print { display: none; }
    }
  </style>
</head>
<body>
  <h1>Patent Landscape Report</h1>
  <p>Generated: ${new Date().toLocaleString()}</p>
  <p>File ID: ${mapData.file_id}</p>

  <div class="summary">
    <div class="summary-grid">
      <div class="stat-box">
        <div class="stat-value">${totalMols.toLocaleString()}</div>
        <div class="stat-label">Total Molecules</div>
      </div>
      <div class="stat-box">
        <div class="stat-value at-risk">${atRiskCount.toLocaleString()}</div>
        <div class="stat-label">At Risk (Zones 1-4)</div>
      </div>
      <div class="stat-box">
        <div class="stat-value novel">${novelCount.toLocaleString()}</div>
        <div class="stat-label">Novel (Zone 5)</div>
      </div>
      <div class="stat-box">
        <div class="stat-value">${atRiskPercent}%</div>
        <div class="stat-label">Risk Percentage</div>
      </div>
    </div>
  </div>

  <h2>Zone Breakdown</h2>
  <table class="zone-table">
    <thead>
      <tr>
        <th>Zone</th>
        <th>Classification</th>
        <th>Risk Level</th>
        <th>Count</th>
        <th>Percentage</th>
      </tr>
    </thead>
    <tbody>
      ${[1, 2, 3, 4, 5].map(zone => {
        const config = ZONE_CONFIG[zone];
        const count = zoneCounts[String(zone)] || 0;
        const pct = totalMols > 0 ? ((count / totalMols) * 100).toFixed(1) : '0';
        return `
          <tr>
            <td><span class="zone-dot" style="background:${config.color}"></span>Zone ${zone}</td>
            <td>${config.label}</td>
            <td>${config.risk}</td>
            <td>${count.toLocaleString()}</td>
            <td>${pct}%</td>
          </tr>
        `;
      }).join('')}
    </tbody>
  </table>

  <h2>At-Risk Molecules (Zones 1-4)</h2>
  <table class="molecule-table">
    <thead>
      <tr>
        <th>Name</th>
        <th>SMILES</th>
        <th>Zone</th>
        <th>Max Similarity</th>
        <th>Top Patent Match</th>
      </tr>
    </thead>
    <tbody>
      ${molecules
        .filter(m => m.zone <= 4)
        .slice(0, 100)
        .map(mol => {
          const config = ZONE_CONFIG[mol.zone];
          const matches = mol.top_patent_matches || mol.patent_matches || [];
          const topMatch = matches[0];
          return `
            <tr>
              <td>${mol.name || `ID: ${mol.id}`}</td>
              <td style="font-family: monospace; font-size: 10px; max-width: 200px; overflow: hidden; text-overflow: ellipsis;">${mol.smiles || ''}</td>
              <td><span class="zone-dot" style="background:${config.color}"></span>${config.label}</td>
              <td>${mol.max_similarity ? `${(mol.max_similarity * 100).toFixed(1)}%` : '-'}</td>
              <td>${topMatch ? `${topMatch.surechembl_id || topMatch.patent_number || 'Unknown'} (${((topMatch.similarity || 0) * 100).toFixed(1)}%)` : '-'}</td>
            </tr>
          `;
        }).join('')}
    </tbody>
  </table>
  ${molecules.filter(m => m.zone <= 4).length > 100 ? `<p><em>Showing first 100 of ${molecules.filter(m => m.zone <= 4).length} at-risk molecules. Export CSV for complete list.</em></p>` : ''}

  <div class="footer">
    <p>Generated by Adroit Patent Landscape</p>
    <p>Data source: SureChEMBL (28.4M patent compounds)</p>
  </div>

  <script>
    window.onload = function() { window.print(); }
  </script>
</body>
</html>`;

    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  // Export to JSON
  const handleExportJSON = () => {
    if (!mapData) {
      alert('No data to export');
      return;
    }

    const jsonContent = JSON.stringify(mapData, null, 2);
    const blob = new Blob([jsonContent], { type: 'application/json' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `patent-collision-data-${mapData.file_id}-${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    URL.revokeObjectURL(link.href);
  };



  // Handle At-Risk Only toggle
  const handleToggleAtRiskOnly = () => {
    setAtRiskOnly(prev => {
      if (!prev) {
        setVisibleZones([1, 2, 3, 4]);
      } else {
        setVisibleZones([1, 2, 3, 4, 5]);
      }
      return !prev;
    });
  };

  // Toggle zone visibility when legend item is clicked
  const toggleZone = (zone: number) => {
    setVisibleZones(prev =>
      prev.includes(zone)
        ? prev.filter(z => z !== zone)
        : [...prev, zone].sort()
    );
  };

  // Canvas refs
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const labelHitAreasRef = useRef<Map<string, { x: number; y: number; width: number; height: number }>>(new Map());

  // Track previous viewMode for transition handling
  const prevViewModeRef = useRef<ViewMode | null>(null);

  // Handle view mode changes - trigger canvas redraw when entering scatter
  useEffect(() => {
    if (viewMode === 'scatter' && prevViewModeRef.current !== 'scatter' && mapData) {
      // Switching to Explorer - ensure animationPhase allows drawing
      // If we came from another view, animationPhase is 'complete', which is correct
      // Force a re-render by slightly updating a state (the viewMode dep will trigger the canvas useEffect)
      // Just need to make sure the canvas container is mounted first
      const timer = setTimeout(() => {
        // Force canvas redraw by triggering a small state change
        setDisplayLimit(prev => prev);
      }, 50);
      return () => clearTimeout(timer);
    }
    prevViewModeRef.current = viewMode;
  }, [viewMode, mapData]);

  // Computed positions
  const [positions, setPositions] = useState<Record<string, Position>>({});
  const [patentTerritories, setPatentTerritories] = useState<Map<string, { x: number; y: number; r: number; count: number }>>(new Map());
  const [patentDotPositions, setPatentDotPositions] = useState<Map<string, { x: number; y: number; compoundId: string; similarity: number }>>(new Map());

  // Load collision map data
  useEffect(() => {
    if (fileId || projectId) {
      loadMapData();
    }
  }, [fileId, projectId]);

  const loadMapData = async () => {
    setLoading(true);
    setAnimationPhase('loading');
    setError(null);
    try {
      const data = await chemistryApi.getCollisionMap(String(fileId), projectId);
      setMapData(data as MapData);
      // Populate shared molecule cache for PatentsTab — clear old entries first
      moleculePatentCache.clear();
      currentCollisionMapFileId = fileId;
      if (data.molecules) {
        for (const mol of data.molecules) {
          moleculePatentCache.set(`${fileId}:${mol.smiles}`, {
            zone: mol.zone,
            patent_count_exact: mol.patent_count_exact || 0,
            patent_count_similar: mol.patent_count_similar || 0,
            max_similarity: mol.max_similarity ?? null,
          });
        }
      }
      computePositions(data.molecules, data.patents);

      // Auto-select view mode based on molecule count and user preference
      const moleculeCount = data.molecules?.length || 0;
      const defaultView = getDefaultView(moleculeCount);
      setViewMode(defaultView);

      // Set display limit based on count
      if (moleculeCount > 1000) {
        setDisplayLimit(500);
      } else {
        setDisplayLimit(Math.min(100, moleculeCount));
      }

      // If enrichment data already exists (cached results), skip animation entirely
      const hasEnrichment = data.summary?.enriched_molecules > 0;
      if (hasEnrichment) {
        // Cached data — show results immediately
        setAnimationPhase('complete');
      } else if (defaultView === 'scatter') {
        // No cached data — show analyzing animation (for scatter view only)
        setAnimationPhase('analyzing');
        animationStartRef.current = performance.now();
      } else {
        setAnimationPhase('complete');
      }
    } catch (err: any) {
      console.error('Failed to load collision map:', err);
      const detail = err.response?.data?.detail;
      const message = typeof detail === 'string' ? detail
        : Array.isArray(detail) ? detail.map((d: any) => d.msg || JSON.stringify(d)).join('; ')
        : 'Failed to load collision map';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  // Compute scattered 2D positions for molecules
  const computePositions = (molecules: MoleculeData[], patents: PatentSummary[]) => {
    if (!molecules?.length) return;

    const width = 800;
    const height = 600;
    const padding = 50;
    const newPositions: Record<string, Position> = {};

    // Track which molecules belong to which patents
    const patentMolecules: Map<string, number[]> = new Map();

    // Phase 1: compute raw hash-based positions for all molecules
    const rawPositions: { id: number; rawX: number; rawY: number; zone?: number }[] = [];

    molecules.forEach((mol, index) => {
      const hash1 = hashString(mol.smiles || `mol-${mol.id}`);
      const hash2 = hashString(`${mol.smiles}-y` || `mol-${mol.id}-y`);

      const baseX = seededRandom(hash1 + index * 7);
      const baseY = seededRandom(hash2 + index * 13);

      const jitterX = seededRandom(index * 31) * 0.1 - 0.05;
      const jitterY = seededRandom(index * 37) * 0.1 - 0.05;

      rawPositions.push({
        id: mol.id,
        rawX: baseX + jitterX,
        rawY: baseY + jitterY,
        zone: mol.zone,
      });

      // Track molecules by patent (support both old and new API format)
      const matches = mol.top_patent_matches || mol.patent_matches || [];
      matches.forEach(pm => {
        const patentId = pm.surechembl_id || pm.patent_number;
        if (!patentId) return;
        if (!patentMolecules.has(patentId)) {
          patentMolecules.set(patentId, []);
        }
        patentMolecules.get(patentId)!.push(mol.id);
      });
    });

    // Phase 2: normalize positions to fill the viewport
    if (rawPositions.length > 0) {
      let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
      for (const p of rawPositions) {
        if (p.rawX < minX) minX = p.rawX;
        if (p.rawX > maxX) maxX = p.rawX;
        if (p.rawY < minY) minY = p.rawY;
        if (p.rawY > maxY) maxY = p.rawY;
      }

      const rangeX = maxX - minX || 1;
      const rangeY = maxY - minY || 1;

      for (const p of rawPositions) {
        // Normalize to 0..1, then map to canvas with padding
        const normX = (p.rawX - minX) / rangeX;
        const normY = (p.rawY - minY) / rangeY;

        const x = padding + (width - 2 * padding) * (0.05 + 0.9 * normX);
        const y = padding + (height - 2 * padding) * (0.05 + 0.9 * normY);

        newPositions[`mol-${p.id}`] = { x, y, zone: p.zone };
      }
    }

    // Compute patent territories as circles encompassing their molecules
    // Radius uses log scale for proportional sizing based on family breadth
    const territories = new Map<string, { x: number; y: number; r: number; count: number }>();

    patentMolecules.forEach((molIds, patentNumber) => {
      if (molIds.length === 0) return;

      // Calculate centroid of molecules
      let sumX = 0, sumY = 0;
      let validCount = 0;
      molIds.forEach(id => {
        const pos = newPositions[`mol-${id}`];
        if (pos) {
          sumX += pos.x;
          sumY += pos.y;
          validCount++;
        }
      });
      if (validCount === 0) return;

      const centerX = sumX / validCount;
      const centerY = sumY / validCount;

      // Proportional sizing: radius = 6 + 6 * log2(compound_count)
      // compound_count here is the number of molecules linked to this patent
      const logRadius = 6 + 6 * Math.log2(Math.max(1, validCount));

      // Also consider spread of molecules
      let maxDist = 0;
      molIds.forEach(id => {
        const pos = newPositions[`mol-${id}`];
        if (pos) {
          const dist = Math.sqrt((pos.x - centerX) ** 2 + (pos.y - centerY) ** 2);
          maxDist = Math.max(maxDist, dist);
        }
      });

      // Scale up from normalized coordinates
      const radius = Math.max(logRadius + maxDist + 10, 20);

      territories.set(patentNumber, {
        x: centerX,
        y: centerY,
        r: Math.min(Math.max(radius, 20), 160),
        count: validCount,
      });
    });

    // Compute individual patent compound dot positions
    // Each unique compound gets a dot positioned near its territory center,
    // offset by distance proportional to (1 - best_similarity)
    const dotPositions = new Map<string, { x: number; y: number; compoundId: string; similarity: number }>();

    molecules.forEach((mol) => {
      const molPos = newPositions[`mol-${mol.id}`];
      if (!molPos) return;
      const matches = mol.top_patent_matches || mol.patent_matches || [];
      matches.forEach(pm => {
        const compoundId = pm.surechembl_id || pm.patent_number;
        if (!compoundId || dotPositions.has(compoundId)) return;
        const similarity = pm.similarity || 0;

        // Distance from molecule proportional to dissimilarity
        const dissimilarity = 1 - similarity;
        const distance = 15 + dissimilarity * 80; // 15-95px in 800x600 space

        // Use hash for consistent angle
        const angle = (hashString(compoundId) % 360) * Math.PI / 180;
        const dotX = molPos.x + Math.cos(angle) * distance;
        const dotY = molPos.y + Math.sin(angle) * distance;

        dotPositions.set(compoundId, {
          x: Math.max(20, Math.min(780, dotX)),
          y: Math.max(20, Math.min(580, dotY)),
          compoundId,
          similarity,
        });
      });
    });

    setPositions(newPositions);
    setPatentTerritories(territories);
    setPatentDotPositions(dotPositions);
  };

  // Animation loop for crystallization effect - only for Explorer view
  useEffect(() => {
    // Only run animation when in Explorer view
    if (viewMode !== 'scatter') return;
    if (animationPhase === 'loading' || animationPhase === 'complete' || !canvasRef.current || !containerRef.current || !mapData) {
      return;
    }

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rect = containerRef.current.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;

    // Set canvas size
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    canvas.style.width = `${rect.width}px`;
    canvas.style.height = `${rect.height}px`;
    ctx.scale(dpr, dpr);

    const scaleX = rect.width / 800;
    const scaleY = rect.height / 600;
    const molecules = mapData.molecules || [];

    const animate = (timestamp: number) => {
      const elapsed = timestamp - animationStartRef.current;

      // Clear canvas with theme-aware background
      const isDark = isDarkMode;
      ctx.fillStyle = isDark ? '#0f172a' : '#f1f5f9';
      ctx.fillRect(0, 0, rect.width, rect.height);

      // Calculate animation phase and progress
      // Phase 1: Analyzing text (0-800ms)
      // Phase 2: Molecules fly in (800-1800ms)
      // Phase 3: Circles draw (1800-2400ms)
      // Phase 4: Lines animate in (2400-2800ms)
      // Complete at 2800ms

      let phase: 'loading' | 'analyzing' | 'molecules' | 'circles' | 'lines' | 'complete' = 'analyzing';
      let phaseProgress = 0;

      if (elapsed < 800) {
        phase = 'analyzing';
        phaseProgress = elapsed / 800;
      } else if (elapsed < 1800) {
        phase = 'molecules';
        phaseProgress = (elapsed - 800) / 1000;
      } else if (elapsed < 2400) {
        phase = 'circles';
        phaseProgress = (elapsed - 1800) / 600;
      } else if (elapsed < 2800) {
        phase = 'lines';
        phaseProgress = (elapsed - 2400) / 400;
      } else {
        phase = 'complete';
      }

      setAnimationPhase(phase);

      // Phase 1: Analyzing text with pulsing glow
      if (phase === 'analyzing') {
        const pulse = Math.sin(elapsed * 0.005) * 0.3 + 0.7;
        const fadeIn = Math.min(1, phaseProgress * 2); // Fade in over first half

        ctx.save();
        ctx.globalAlpha = fadeIn * pulse;
        ctx.font = '16px Inter, system-ui, sans-serif';
        ctx.fillStyle = '#60a5fa';
        ctx.textAlign = 'center';
        ctx.shadowColor = '#3b82f6';
        ctx.shadowBlur = 20 * pulse;
        ctx.fillText('Analyzing against 28.4M patent compounds...', rect.width / 2, rect.height / 2);
        ctx.restore();
      }

      // Phase 2+: Draw molecules (fly in from edges with shimmer)
      if (phase !== 'analyzing') {
        const moleculeProgress = phase === 'molecules' ? easeInOutCubic(phaseProgress) : 1;
        const shimmerActive = phase === 'molecules' && phaseProgress > 0.7;

        molecules.forEach((mol, index) => {
          const pos = positions[`mol-${mol.id}`];
          if (!pos) return;

          const targetX = pos.x * scaleX;
          const targetY = pos.y * scaleY;

          // Calculate starting position (from nearest edge)
          const centerX = rect.width / 2;
          const centerY = rect.height / 2;
          const angle = Math.atan2(targetY - centerY, targetX - centerX);
          const edgeDist = Math.max(rect.width, rect.height);
          const startX = centerX + Math.cos(angle) * edgeDist;
          const startY = centerY + Math.sin(angle) * edgeDist;

          // Stagger the molecules slightly based on index
          const staggerDelay = (index % 20) * 0.02;
          const adjustedProgress = Math.max(0, Math.min(1, (moleculeProgress - staggerDelay) / (1 - staggerDelay)));

          const x = startX + (targetX - startX) * adjustedProgress;
          const y = startY + (targetY - startY) * adjustedProgress;

          const zone = mol.zone || 5;
          const zoneColor = ZONE_CONFIG[zone]?.color || '#22c55e';

          // Shimmer effect at end of molecule phase
          let opacity = adjustedProgress;
          if (shimmerActive) {
            const shimmer = Math.sin(index * 0.3 + phaseProgress * Math.PI * 4) * 0.2 + 0.8;
            opacity *= shimmer;
          }

          // Draw molecule dot with glow
          ctx.save();
          ctx.globalAlpha = opacity;
          ctx.shadowColor = zoneColor;
          ctx.shadowBlur = 12;
          ctx.beginPath();
          ctx.arc(x, y, 5, 0, 2 * Math.PI);
          ctx.fillStyle = zoneColor;
          ctx.fill();
          ctx.restore();
        });
      }

      // Phase 3+: Draw patent territory circles and grey compound dots
      if (phase === 'circles' || phase === 'lines' || phase === 'complete') {
        const circleProgress = phase === 'circles' ? easeInOutCubic(phaseProgress) : 1;

        patentTerritories.forEach((territory) => {
          const x = territory.x * scaleX;
          const y = territory.y * scaleY;
          const r = territory.r * Math.min(scaleX, scaleY);

          ctx.beginPath();
          ctx.arc(x, y, r, -Math.PI / 2, -Math.PI / 2 + 2 * Math.PI * circleProgress);
          ctx.strokeStyle = `rgba(148, 163, 184, ${0.5 * circleProgress})`;
          ctx.lineWidth = 1.5;
          ctx.setLineDash([6, 4]);
          ctx.stroke();
          ctx.setLineDash([]);
        });

        // Grey patent compound dots fade in during circles phase
        patentDotPositions.forEach((dot) => {
          const x = dot.x * scaleX;
          const y = dot.y * scaleY;
          ctx.beginPath();
          ctx.arc(x, y, 4, 0, 2 * Math.PI);
          ctx.fillStyle = `rgba(148, 163, 184, ${0.6 * circleProgress})`;
          ctx.fill();
        });
      }

      // Phase 4+: Draw connection lines to patent compound dots
      if (phase === 'lines' || phase === 'complete') {
        const lineProgress = phase === 'lines' ? easeInOutCubic(phaseProgress) : 1;

        molecules.forEach((mol) => {
          const molPos = positions[`mol-${mol.id}`];
          if (!molPos) return;

          const matches = mol.top_patent_matches || mol.patent_matches || [];
          if (!matches.length) return;

          const bestMatch = matches.reduce((best, curr) =>
            (curr.similarity || 0) > (best.similarity || 0) ? curr : best
          , matches[0]);

          const compoundId = bestMatch.surechembl_id || bestMatch.patent_number;
          if (!compoundId) return;

          const dot = patentDotPositions.get(compoundId);
          const endPos = dot || patentTerritories.get(compoundId);
          if (!endPos) return;

          const startX = molPos.x * scaleX;
          const startY = molPos.y * scaleY;
          const endX = endPos.x * scaleX;
          const endY = endPos.y * scaleY;

          const currentEndX = startX + (endX - startX) * lineProgress;
          const currentEndY = startY + (endY - startY) * lineProgress;

          const similarity = bestMatch.similarity || 0;
          const lineWidth = similarity >= 0.9 ? 3 : similarity >= 0.75 ? 2 : 1;

          const zoneColor = ZONE_CONFIG[mol.zone]?.color || '#facc15';
          const r = parseInt(zoneColor.slice(1, 3), 16);
          const g = parseInt(zoneColor.slice(3, 5), 16);
          const b = parseInt(zoneColor.slice(5, 7), 16);

          ctx.beginPath();
          ctx.moveTo(startX, startY);
          ctx.lineTo(currentEndX, currentEndY);
          ctx.strokeStyle = `rgba(${r}, ${g}, ${b}, ${0.4 * lineProgress})`;
          ctx.lineWidth = lineWidth;
          ctx.stroke();
        });
      }

      // Continue animation or complete
      if (phase !== 'complete') {
        animationFrameRef.current = requestAnimationFrame(animate);
      }
    };

    animationFrameRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [animationPhase, mapData, positions, patentTerritories, patentDotPositions]);

  // Main canvas drawing (after animation completes) - only for Explorer view
  useEffect(() => {
    // Only draw canvas when in Explorer view
    if (viewMode !== 'scatter') return;
    if (animationPhase !== 'complete' || !canvasRef.current || !mapData || !containerRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const rect = containerRef.current.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    canvas.style.width = `${rect.width}px`;
    canvas.style.height = `${rect.height}px`;
    ctx.scale(dpr, dpr);

    // Clear with theme-aware background
    const isDark = isDarkMode;
    ctx.fillStyle = isDark ? '#0f172a' : '#f1f5f9';
    ctx.fillRect(0, 0, rect.width, rect.height);

    // Clear label hit areas before redrawing
    labelHitAreasRef.current.clear();

    const scaleX = rect.width / 800;
    const scaleY = rect.height / 600;

    // Filter molecules by visible zones and apply display limit
    const filteredMolecules = (mapData.molecules || []).filter(mol => visibleZones.includes(mol.zone));
    const visibleMolecules = filteredMolecules.slice(0, displayLimit);

    // HEATMAP MODE for large datasets
    if (explorerSubMode === 'heatmap') {
      const gridSize = 20; // 20x20 grid
      const cellWidth = rect.width / gridSize;
      const cellHeight = rect.height / gridSize;

      // Build heatmap grid
      const grid: { count: number; zones: number[] }[][] = [];
      for (let i = 0; i < gridSize; i++) {
        grid[i] = [];
        for (let j = 0; j < gridSize; j++) {
          grid[i][j] = { count: 0, zones: [] };
        }
      }

      // Populate grid with molecules
      filteredMolecules.forEach(mol => {
        const pos = positions[`mol-${mol.id}`];
        if (!pos) return;
        const gx = Math.min(gridSize - 1, Math.floor((pos.x * scaleX) / cellWidth));
        const gy = Math.min(gridSize - 1, Math.floor((pos.y * scaleY) / cellHeight));
        grid[gx][gy].count++;
        grid[gx][gy].zones.push(mol.zone);
      });

      // Find max count for normalization
      let maxCount = 1;
      for (let i = 0; i < gridSize; i++) {
        for (let j = 0; j < gridSize; j++) {
          maxCount = Math.max(maxCount, grid[i][j].count);
        }
      }

      // Draw heatmap cells
      for (let i = 0; i < gridSize; i++) {
        for (let j = 0; j < gridSize; j++) {
          const cell = grid[i][j];
          if (cell.count === 0) continue;

          const intensity = cell.count / maxCount;
          // Determine dominant zone color
          const zoneCounts: Record<number, number> = {};
          cell.zones.forEach(z => { zoneCounts[z] = (zoneCounts[z] || 0) + 1; });
          let dominantZone = 5;
          let maxZoneCount = 0;
          Object.entries(zoneCounts).forEach(([z, c]) => {
            if (c > maxZoneCount) {
              maxZoneCount = c;
              dominantZone = parseInt(z);
            }
          });

          const zoneColor = ZONE_CONFIG[dominantZone]?.color || '#22c55e';
          const r = parseInt(zoneColor.slice(1, 3), 16);
          const g = parseInt(zoneColor.slice(3, 5), 16);
          const b = parseInt(zoneColor.slice(5, 7), 16);

          ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${0.2 + intensity * 0.6})`;
          ctx.fillRect(i * cellWidth + 1, j * cellHeight + 1, cellWidth - 2, cellHeight - 2);

          // Add count label for high-density cells
          if (cell.count > maxCount * 0.3) {
            ctx.font = '10px Inter, system-ui, sans-serif';
            ctx.fillStyle = isDark ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.7)';
            ctx.textAlign = 'center';
            ctx.fillText(
              cell.count.toLocaleString(),
              i * cellWidth + cellWidth / 2,
              j * cellHeight + cellHeight / 2 + 4
            );
          }
        }
      }

      // Draw heatmap legend
      ctx.font = '12px Inter, system-ui, sans-serif';
      ctx.fillStyle = isDark ? '#94a3b8' : '#475569';
      ctx.textAlign = 'left';
      ctx.fillText(`Heatmap: ${filteredMolecules.length.toLocaleString()} molecules`, 10, 20);
      ctx.fillText('Color intensity = molecule density', 10, 36);

      return; // Skip scatter drawing in heatmap mode
    }

    // ═══════════════════════════════════════════════════════════
    // MODE 1: MY MOLECULES → PATENTS (default)
    // Your molecules are focal points, patent compounds are context
    // ═══════════════════════════════════════════════════════════
    if (collisionViewMode === 'my-molecules') {
      // Draw patent territory circles (dashed)
      patentTerritories.forEach((territory, patentId) => {
        const x = territory.x * scaleX;
        const y = territory.y * scaleY;
        const r = territory.r * Math.min(scaleX, scaleY);
        const isSelected = selectedPatent?.patent_number === patentId;

        ctx.beginPath();
        ctx.arc(x, y, r, 0, 2 * Math.PI);
        ctx.strokeStyle = isSelected ? '#3b82f6' : (isDark ? 'rgba(148, 163, 184, 0.3)' : 'rgba(100, 116, 139, 0.3)');
        ctx.lineWidth = isSelected ? 2 : 1;
        ctx.setLineDash([6, 4]);
        ctx.stroke();
        ctx.setLineDash([]);

        if (isSelected) {
          ctx.fillStyle = 'rgba(59, 130, 246, 0.15)';
          ctx.fill();
        }

        // Label
        const label = patentId.length > 14 ? patentId.slice(0, 11) + '...' : patentId;
        const labelY = y - r - 8;
        ctx.font = '9px Inter, system-ui, sans-serif';
        ctx.textAlign = 'center';
        const textWidth = ctx.measureText(label).width;
        const labelBgX = x - textWidth / 2 - 3;
        const labelBgY = labelY - 8;
        const labelBgWidth = textWidth + 6;
        const labelBgHeight = 12;

        ctx.fillStyle = isDark ? 'rgba(15, 23, 42, 0.8)' : 'rgba(255, 255, 255, 0.85)';
        ctx.fillRect(labelBgX, labelBgY, labelBgWidth, labelBgHeight);

        labelHitAreasRef.current.set(patentId, { x: labelBgX, y: labelBgY, width: labelBgWidth, height: labelBgHeight });
        ctx.fillStyle = isSelected ? '#2563eb' : (isDark ? '#93c5fd' : '#3b82f6');
        ctx.fillText(label, x, labelY);

        ctx.beginPath();
        ctx.moveTo(x - textWidth / 2, labelY + 2);
        ctx.lineTo(x + textWidth / 2, labelY + 2);
        ctx.strokeStyle = isSelected ? '#2563eb' : (isDark ? 'rgba(147, 197, 253, 0.5)' : 'rgba(59, 130, 246, 0.5)');
        ctx.lineWidth = 0.5;
        ctx.stroke();

        ctx.font = '8px Inter, system-ui, sans-serif';
        ctx.fillStyle = isDark ? 'rgba(148, 163, 184, 0.6)' : 'rgba(71, 85, 105, 0.7)';
        ctx.fillText(`${territory.count} mol`, x, labelY + 10);
      });

      // Draw grey patent compound dots (small, contextual)
      patentDotPositions.forEach((dot, compoundId) => {
        const x = dot.x * scaleX;
        const y = dot.y * scaleY;
        const territory = patentTerritories.get(compoundId);
        const familyCount = territory?.count || 1;
        const dotRadius = Math.min(30, Math.max(3, 6 + 6 * Math.log2(Math.max(1, familyCount))));

        ctx.save();
        ctx.globalAlpha = 0.6;
        ctx.shadowColor = isDark ? 'rgba(148, 163, 184, 0.3)' : 'rgba(100, 116, 139, 0.2)';
        ctx.shadowBlur = 4;
        ctx.beginPath();
        ctx.arc(x, y, dotRadius, 0, 2 * Math.PI);
        ctx.fillStyle = '#6b7280';
        ctx.fill();
        ctx.restore();

        // SCHEMBL label (small grey text)
        const label = compoundId.length > 16 ? compoundId.slice(0, 13) + '...' : compoundId;
        ctx.font = '10px Inter, system-ui, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillStyle = isDark ? 'rgba(148, 163, 184, 0.6)' : 'rgba(100, 116, 139, 0.6)';
        ctx.fillText(label, x, y + dotRadius + 12);
      });

      // Connection lines: molecule → patent ONLY (no patent-to-patent)
      // Line thickness by similarity: >=0.90 = 3px, 0.75-0.89 = 2px, <0.75 = 1px dashed
      visibleMolecules.forEach((mol) => {
        const molPos = positions[`mol-${mol.id}`];
        if (!molPos) return;

        const matches = mol.top_patent_matches || mol.patent_matches || [];
        if (!matches.length) return;

        const zoneColor = ZONE_CONFIG[mol.zone]?.color || '#facc15';
        const isSelectedMol = selectedMolecule?.id === mol.id;
        const rr = parseInt(zoneColor.slice(1, 3), 16);
        const gg = parseInt(zoneColor.slice(3, 5), 16);
        const bb = parseInt(zoneColor.slice(5, 7), 16);

        matches.forEach(pm => {
          const compoundId = pm.surechembl_id || pm.patent_number;
          if (!compoundId) return;

          const dot = patentDotPositions.get(compoundId);
          if (!dot) return;

          const similarity = pm.similarity || 0;
          const lineWidth = similarity >= 0.9 ? 3 : similarity >= 0.75 ? 2 : 1;

          ctx.beginPath();
          ctx.moveTo(molPos.x * scaleX, molPos.y * scaleY);
          ctx.lineTo(dot.x * scaleX, dot.y * scaleY);
          ctx.strokeStyle = `rgba(${rr}, ${gg}, ${bb}, ${isSelectedMol ? 0.7 : 0.4})`;
          ctx.lineWidth = isSelectedMol ? lineWidth + 1 : lineWidth;
          if (similarity < 0.75) ctx.setLineDash([4, 3]);
          ctx.stroke();
          ctx.setLineDash([]);
        });
      });

      // Draw molecule dots (PRIMARY — large, prominent, white border)
      visibleMolecules.forEach((mol) => {
        const pos = positions[`mol-${mol.id}`];
        if (!pos) return;

        const x = pos.x * scaleX;
        const y = pos.y * scaleY;
        const zone = mol.zone || 5;
        const isSelected = selectedMolecule?.id === mol.id;
        const isHovered = hoveredItem?.type === 'molecule' && hoveredItem?.id === mol.id;
        const zoneColor = ZONE_CONFIG[zone]?.color || '#22c55e';

        // Larger radius: 10-14px range
        const radius = isSelected ? 14 : isHovered ? 12 : 10;

        ctx.save();
        ctx.shadowColor = zoneColor;
        ctx.shadowBlur = isSelected ? 25 : isHovered ? 18 : 12;

        ctx.beginPath();
        ctx.arc(x, y, radius, 0, 2 * Math.PI);
        ctx.fillStyle = zoneColor;
        ctx.fill();

        // White border (2px) to stand out
        ctx.shadowBlur = 0;
        ctx.strokeStyle = isSelected ? '#ffffff' : (isDark ? 'rgba(255, 255, 255, 0.8)' : 'rgba(255, 255, 255, 0.9)');
        ctx.lineWidth = 2;
        ctx.stroke();

        ctx.restore();

        // Label with molecule name below the dot
        const molName = mol.name || `Mol ${mol.id}`;
        const displayName = molName.length > 15 ? molName.slice(0, 12) + '...' : molName;
        ctx.font = '10px Inter, system-ui, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillStyle = isDark ? 'rgba(255, 255, 255, 0.8)' : 'rgba(15, 23, 42, 0.8)';
        ctx.fillText(displayName, x, y + radius + 14);
      });

      // Highlighted connections for selected molecule
      const selectedMatches = selectedMolecule?.top_patent_matches || selectedMolecule?.patent_matches || [];
      if (selectedMatches.length) {
        const molPos = positions[`mol-${selectedMolecule!.id}`];
        if (molPos) {
          selectedMatches.slice(0, 5).forEach((pm) => {
            const patentId = pm.surechembl_id || pm.patent_number;
            if (!patentId) return;
            const territory = patentTerritories.get(patentId);
            if (!territory) return;

            ctx.beginPath();
            ctx.moveTo(molPos.x * scaleX, molPos.y * scaleY);
            ctx.lineTo(territory.x * scaleX, territory.y * scaleY);
            const zone = selectedMolecule!.zone || 5;
            const zoneColor = ZONE_CONFIG[zone]?.color || '#facc15';
            ctx.strokeStyle = zoneColor;
            ctx.lineWidth = 3;
            ctx.setLineDash([6, 3]);
            ctx.stroke();
            ctx.setLineDash([]);
          });
        }
      }

    // ═══════════════════════════════════════════════════════════
    // MODE 2: PATENT LANDSCAPE
    // Patent families are focal points, your molecules are markers
    // ═══════════════════════════════════════════════════════════
    } else {
      // Group patent compounds by patent_number (family)
      const patentFamilies = new Map<string, {
        patentNumber: string;
        compounds: string[];
        moleculeIds: Set<number>;
        zones: number[];
        positions: { x: number; y: number }[];
      }>();

      visibleMolecules.forEach(mol => {
        const matches = mol.top_patent_matches || mol.patent_matches || [];
        matches.forEach(pm => {
          const patentNum = pm.patent_number || pm.surechembl_id || '';
          if (!patentNum) return;
          if (!patentFamilies.has(patentNum)) {
            patentFamilies.set(patentNum, {
              patentNumber: patentNum,
              compounds: [],
              moleculeIds: new Set(),
              zones: [],
              positions: [],
            });
          }
          const family = patentFamilies.get(patentNum)!;
          if (pm.surechembl_id && !family.compounds.includes(pm.surechembl_id)) {
            family.compounds.push(pm.surechembl_id);
          }
          family.moleculeIds.add(mol.id);
          family.zones.push(mol.zone);

          // Collect dot positions for this compound
          const dot = patentDotPositions.get(pm.surechembl_id || patentNum);
          if (dot) family.positions.push({ x: dot.x, y: dot.y });
        });
      });

      // Compute family centroids and determine colors
      const familyCentroids = new Map<string, { x: number; y: number; radius: number; color: string; label: string; compoundCount: number }>();

      patentFamilies.forEach((family, patentNum) => {
        // Use compound dot positions if available, otherwise territory positions
        let posArray = family.positions;
        if (posArray.length === 0) {
          const territory = patentTerritories.get(patentNum);
          if (territory) posArray = [{ x: territory.x, y: territory.y }];
        }
        if (posArray.length === 0) return;

        // Centroid
        let sumX = 0, sumY = 0;
        posArray.forEach(p => { sumX += p.x; sumY += p.y; });
        const cx = sumX / posArray.length;
        const cy = sumY / posArray.length;

        // Size by compound count (log scale)
        const compoundsInFamily = Math.max(1, family.compounds.length || family.moleculeIds.size);
        const radius = Math.min(50, 10 + 8 * Math.log2(Math.max(1, compoundsInFamily)));

        // Color by most dangerous zone
        const minZone = family.zones.length > 0 ? Math.min(...family.zones) : 0;
        const zoneColorMap: Record<number, string> = {
          1: '#dc2626', 2: '#ea580c', 3: '#ca8a04', 4: '#2563eb', 5: '#16a34a',
        };
        const color = minZone > 0 ? (zoneColorMap[minZone] || '#9ca3af') : '#9ca3af';

        // Label: use patent number if available
        const label = patentNum.includes('SCHEMBL') ? 'SCHEMBL cluster' : patentNum;

        familyCentroids.set(patentNum, { x: cx, y: cy, radius, color, label, compoundCount: compoundsInFamily });
      });

      // Draw patent-to-patent connections (families sharing similar chemistry)
      // Two patents are connected if any molecule matches both
      const familyKeys = Array.from(familyCentroids.keys());
      for (let i = 0; i < familyKeys.length; i++) {
        for (let j = i + 1; j < familyKeys.length; j++) {
          const familyA = patentFamilies.get(familyKeys[i]);
          const familyB = patentFamilies.get(familyKeys[j]);
          if (!familyA || !familyB) continue;

          // Count shared molecules
          let sharedCount = 0;
          familyA.moleculeIds.forEach(id => {
            if (familyB.moleculeIds.has(id)) sharedCount++;
          });

          if (sharedCount === 0) continue;

          const centA = familyCentroids.get(familyKeys[i])!;
          const centB = familyCentroids.get(familyKeys[j])!;

          const lineThickness = Math.min(4, 1 + sharedCount * 0.5);
          ctx.beginPath();
          ctx.moveTo(centA.x * scaleX, centA.y * scaleY);
          ctx.lineTo(centB.x * scaleX, centB.y * scaleY);
          ctx.strokeStyle = isDark ? `rgba(148, 163, 184, ${Math.min(0.5, 0.15 + sharedCount * 0.08)})` : `rgba(100, 116, 139, ${Math.min(0.5, 0.15 + sharedCount * 0.08)})`;
          ctx.lineWidth = lineThickness;
          ctx.stroke();
        }
      }

      // Draw territory circles around each patent family
      familyCentroids.forEach((centroid) => {
        const x = centroid.x * scaleX;
        const y = centroid.y * scaleY;
        const r = (centroid.radius + 15) * Math.min(scaleX, scaleY);

        ctx.beginPath();
        ctx.arc(x, y, r, 0, 2 * Math.PI);
        ctx.strokeStyle = isDark ? 'rgba(148, 163, 184, 0.15)' : 'rgba(100, 116, 139, 0.15)';
        ctx.lineWidth = 1;
        ctx.setLineDash([8, 5]);
        ctx.stroke();
        ctx.setLineDash([]);
      });

      // Draw patent family circles (PRIMARY — large, colored, labelled)
      familyCentroids.forEach((centroid, patentNum) => {
        const x = centroid.x * scaleX;
        const y = centroid.y * scaleY;
        const r = centroid.radius;

        ctx.save();
        ctx.shadowColor = centroid.color;
        ctx.shadowBlur = 15;

        ctx.beginPath();
        ctx.arc(x, y, r, 0, 2 * Math.PI);
        ctx.fillStyle = centroid.color;
        ctx.globalAlpha = 0.7;
        ctx.fill();
        ctx.globalAlpha = 1;

        // White border
        ctx.shadowBlur = 0;
        ctx.strokeStyle = isDark ? 'rgba(255, 255, 255, 0.8)' : 'rgba(255, 255, 255, 0.9)';
        ctx.lineWidth = 2;
        ctx.stroke();
        ctx.restore();

        // Patent number label below circle
        const displayLabel = centroid.label.length > 18 ? centroid.label.slice(0, 15) + '...' : centroid.label;
        ctx.font = '10px Inter, system-ui, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillStyle = isDark ? 'rgba(255, 255, 255, 0.8)' : 'rgba(15, 23, 42, 0.85)';
        ctx.fillText(displayLabel, x, y + r + 14);

        // Compound count inside the circle
        ctx.font = 'bold 11px Inter, system-ui, sans-serif';
        ctx.fillStyle = '#ffffff';
        ctx.fillText(`${centroid.compoundCount}`, x, y + 4);

        // Store hit area
        labelHitAreasRef.current.set(patentNum, { x: x - r, y: y - r, width: r * 2, height: r * 2 });
      });

      // Draw YOUR MOLECULES as small bright markers (secondary)
      visibleMolecules.forEach((mol) => {
        const pos = positions[`mol-${mol.id}`];
        if (!pos) return;

        const x = pos.x * scaleX;
        const y = pos.y * scaleY;
        const zone = mol.zone || 5;
        const isSelected = selectedMolecule?.id === mol.id;
        const isHovered = hoveredItem?.type === 'molecule' && hoveredItem?.id === mol.id;
        const zoneColor = ZONE_CONFIG[zone]?.color || '#22c55e';

        // Small bright circles (6px) with white border
        const radius = isSelected ? 8 : isHovered ? 7 : 6;

        ctx.save();
        ctx.shadowColor = '#ffffff';
        ctx.shadowBlur = isSelected ? 12 : 6;

        ctx.beginPath();
        ctx.arc(x, y, radius, 0, 2 * Math.PI);
        ctx.fillStyle = zoneColor;
        ctx.fill();

        // Bright white border to differentiate from patent circles
        ctx.shadowBlur = 0;
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = isSelected ? 2.5 : 1.5;
        ctx.stroke();
        ctx.restore();

        // Label in bright text
        const molName = mol.name || `Mol ${mol.id}`;
        const displayName = molName.length > 12 ? molName.slice(0, 9) + '...' : molName;
        ctx.font = 'bold 9px Inter, system-ui, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillStyle = isDark ? '#e2e8f0' : '#1e293b';
        ctx.fillText(displayName, x, y + radius + 11);
      });
    }

  }, [animationPhase, mapData, positions, patentTerritories, patentDotPositions, selectedMolecule, selectedPatent, hoveredItem, visibleZones, displayLimit, explorerSubMode, viewMode, collisionViewMode, zoomLevel, isDarkMode]);

  // Wheel zoom for scatter view (non-passive so we can preventDefault)
  useEffect(() => {
    const container = containerRef.current;
    if (!container || viewMode !== 'scatter') return;
    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      const rect = container.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;
      const contentX = (mouseX - panOffsetX) / zoomLevel;
      const contentY = (mouseY - panOffsetY) / zoomLevel;
      const factor = e.deltaY < 0 ? 1.1 : 1 / 1.1;
      const newZoom = Math.min(5, Math.max(0.3, zoomLevel * factor));
      setPanOffsetX(mouseX - contentX * newZoom);
      setPanOffsetY(mouseY - contentY * newZoom);
      setZoomLevel(newZoom);
    };
    container.addEventListener('wheel', handleWheel, { passive: false });
    return () => container.removeEventListener('wheel', handleWheel);
  }, [viewMode, zoomLevel, panOffsetX, panOffsetY]);

  // Drag pan handlers for scatter view
  const handleScatterMouseDown = useCallback((e: React.MouseEvent) => {
    if (viewMode !== 'scatter') return;
    panStartRef.current = { x: e.clientX, y: e.clientY, originX: panOffsetX, originY: panOffsetY };
    didDragRef.current = false;
  }, [viewMode, panOffsetX, panOffsetY]);

  const handleScatterMouseMove = useCallback((e: React.MouseEvent) => {
    if (!panStartRef.current) return;
    const dx = e.clientX - panStartRef.current.x;
    const dy = e.clientY - panStartRef.current.y;
    if (!didDragRef.current && (Math.abs(dx) > 3 || Math.abs(dy) > 3)) {
      didDragRef.current = true;
      setIsPanning(true);
    }
    if (didDragRef.current) {
      setPanOffsetX(panStartRef.current.originX + dx);
      setPanOffsetY(panStartRef.current.originY + dy);
    }
  }, []);

  const handleScatterMouseUp = useCallback(() => {
    panStartRef.current = null;
    setIsPanning(false);
  }, []);

  // Handle canvas click
  const handleCanvasClick = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    if (didDragRef.current) return;
    if (!canvasRef.current || !mapData || animationPhase !== 'complete') return;

    const rect = canvasRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / zoomLevel;
    const y = (e.clientY - rect.top) / zoomLevel;
    const scaleX = (rect.width / zoomLevel) / 800;
    const scaleY = (rect.height / zoomLevel) / 600;

    // Check patent labels first (they're on top)
    const labelEntries = Array.from(labelHitAreasRef.current.entries());
    for (let i = 0; i < labelEntries.length; i++) {
      const [patentId, hitArea] = labelEntries[i];
      if (
        x >= hitArea.x &&
        x <= hitArea.x + hitArea.width &&
        y >= hitArea.y &&
        y <= hitArea.y + hitArea.height
      ) {
        // Open compound detail modal instead of direct external navigation
        setCompoundDetail({ id: String(patentId) });
        return;
      }
    }

    // Filter visible molecules
    const filteredMolecules = (mapData.molecules || []).filter(mol => visibleZones.includes(mol.zone));
    const visibleMolecules = filteredMolecules.slice(0, displayLimit);

    // Check molecules second
    for (const mol of visibleMolecules) {
      const pos = positions[`mol-${mol.id}`];
      if (!pos) continue;

      const dx = x - pos.x * scaleX;
      const dy = y - pos.y * scaleY;
      if (Math.sqrt(dx * dx + dy * dy) < 18) {
        // Toggle pin (add if missing, remove if present)
        togglePin(mol);
        // Always set as active (most recently clicked)
        setSelectedMolecule(mol);
        setSelectedPatent(null);
        // Store for grid scroll when switching views
        if (mol?.id) setPendingScrollMoleculeId(mol.id);
        // Broadcast to popout window
        patentMapChannelRef.current?.postMessage({ type: 'select-molecule', molecule: mol });
        return;
      }
    }

    // Check patent territories
    const territoriesArray = Array.from(patentTerritories.entries());
    for (const [patentId, territory] of territoriesArray) {
      const dx = x - territory.x * scaleX;
      const dy = y - territory.y * scaleY;
      const r = territory.r * Math.min(scaleX, scaleY);
      if (Math.sqrt(dx * dx + dy * dy) < r) {
        // Find molecules that match this patent to compute stats
        const matchingMols = (mapData.molecules || []).filter(mol => {
          const matches = mol.top_patent_matches || mol.patent_matches || [];
          return matches.some(pm => (pm.surechembl_id || pm.patent_number) === patentId);
        });

        // Create a patent summary object
        const patentSummary: PatentSummary = {
          patent_number: patentId,
          patent_title: `SureChEMBL Compound ${patentId}`,
          match_count: territory.count,
          exact_count: matchingMols.filter(m => m.zone === 1).length,
          similar_count: matchingMols.filter(m => m.zone > 1 && m.zone < 5).length,
        };

        setSelectedPatent(patentSummary);
        setSelectedMolecule(null);
        return;
      }
    }

    setSelectedMolecule(null);
    setSelectedPatent(null);
  }, [mapData, positions, patentTerritories, animationPhase, visibleZones, displayLimit, zoomLevel]);

  // Handle mouse move for hover
  const handleCanvasMove = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    if (panStartRef.current && didDragRef.current) return;
    if (!canvasRef.current || !mapData || animationPhase !== 'complete') return;

    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX - rect.left) / zoomLevel;
    const y = (e.clientY - rect.top) / zoomLevel;
    const scaleX = (rect.width / zoomLevel) / 800;
    const scaleY = (rect.height / zoomLevel) / 600;

    // Check if hovering over a patent label (show pointer cursor)
    const labelHitAreas = Array.from(labelHitAreasRef.current.values());
    for (let i = 0; i < labelHitAreas.length; i++) {
      const hitArea = labelHitAreas[i];
      if (
        x >= hitArea.x &&
        x <= hitArea.x + hitArea.width &&
        y >= hitArea.y &&
        y <= hitArea.y + hitArea.height
      ) {
        canvas.style.cursor = 'pointer';
        setHoveredItem(null);
        return;
      }
    }

    // Filter visible molecules
    const filteredMolecules = (mapData.molecules || []).filter(mol => visibleZones.includes(mol.zone));
    const visibleMolecules = filteredMolecules.slice(0, displayLimit);

    // Check molecules
    for (const mol of visibleMolecules) {
      const pos = positions[`mol-${mol.id}`];
      if (!pos) continue;

      const dx = x - pos.x * scaleX;
      const dy = y - pos.y * scaleY;
      if (Math.sqrt(dx * dx + dy * dy) < 18) {
        canvas.style.cursor = 'pointer';
        setHoveredItem({ type: 'molecule', id: mol.id, data: mol });
        return;
      }
    }

    canvas.style.cursor = 'crosshair';
    setHoveredItem(null);
  }, [mapData, positions, animationPhase, visibleZones, displayLimit, zoomLevel]);

  // Zone stats - calculate directly from molecules for accuracy
  const molecules = mapData?.molecules || [];
  const zoneCounts: Record<number, number> = {
    1: molecules.filter(m => m.zone === 1).length,
    2: molecules.filter(m => m.zone === 2).length,
    3: molecules.filter(m => m.zone === 3).length,
    4: molecules.filter(m => m.zone === 4).length,
    5: molecules.filter(m => m.zone === 5).length,
  };
  const totalMolecules = molecules.length;

  // Per-jurisdiction zone breakdown — derived from molecules' patent_matches
  const jurisdictionZones = useMemo(() => {
    const jzMap: Record<string, Record<number, number>> = {};
    for (const mol of molecules) {
      const matches = mol.top_patent_matches || mol.patent_matches || [];
      for (const pm of matches) {
        const pn = pm.patent_number || '';
        const jurCode = pn.match(/^[A-Z]+/)?.[0] || '';
        if (jurCode && jurCode !== 'SCHEMBL') {
          if (!jzMap[jurCode]) jzMap[jurCode] = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
          jzMap[jurCode][mol.zone] = (jzMap[jurCode][mol.zone] || 0) + 1;
        }
      }
    }
    return jzMap;
  }, [molecules]);

  // Highest-risk zone for a jurisdiction (lowest zone number with count > 0)
  const getJurisdictionZone = (jurCode: string): number => {
    const zones = jurisdictionZones[jurCode];
    if (!zones) return 5;
    for (let z = 1; z <= 5; z++) { if (zones[z] > 0) return z; }
    return 5;
  };

  const enrichedCount = molecules.filter(m => m.enriched_at).length || mapData?.summary?.enriched_molecules || totalMolecules;
  const patentCount = mapData?.summary?.total_patents || patentDotPositions.size || patentTerritories.size || 0;

  // Show molecule count slider if 50+ molecules
  const showMoleculeSlider = totalMolecules >= 50;
  const maxDisplay = Math.min(500, totalMolecules);
  const minDisplay = Math.min(50, totalMolecules);

  if (loading) {
    return (
      <LoadingIndicator fullPage={fullPage} />
    );
  }

  if (error) {
    return (
      <div className={`flex flex-col items-center justify-center h-full min-h-[400px] text-red-400 ${fullPage ? '' : 'rounded-xl'}`} style={{ backgroundColor: 'var(--content-bg)' }}>
        <span>{error}</span>
        <button onClick={loadMapData} className="mt-4 px-4 py-2 rounded-lg" style={{ backgroundColor: 'var(--hover-bg)', color: 'var(--text-primary)' }}>
          Retry
        </button>
      </div>
    );
  }

  // No enrichment data — prompt user to enrich first
  if (mapData && mapData.summary?.enriched_molecules === 0 && mapData.summary?.pending_molecules > 0) {
    return (
      <div className={`flex flex-col items-center justify-center h-full min-h-[400px] ${fullPage ? '' : 'rounded-xl'}`} style={{ backgroundColor: 'var(--content-bg)' }}>
        <svg className="w-16 h-16 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: 'var(--text-muted)' }}>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
        <div className="font-medium text-base" style={{ color: 'var(--text-primary)' }}>No patent data yet</div>
        <div className="text-sm mt-2 max-w-sm text-center" style={{ color: 'var(--text-secondary)' }}>
          Click <strong>Enrich</strong> → <strong>Patent Search</strong> to analyse {mapData.summary.pending_molecules} molecules against 28.4M patent compounds.
        </div>
      </div>
    );
  }

  return (
    <div className={`flex flex-col h-full overflow-hidden ${fullPage ? '' : 'rounded-xl'}`} style={{ borderTop: '2px solid #14b8a6', backgroundColor: 'var(--content-bg)', color: 'var(--text-primary)' }}>
      {/* Zone summary strip — collapsible, with molecule/patent stats */}
      <div className="flex items-center gap-1 px-4 py-1.5 border-b overflow-x-auto" style={{ backgroundColor: 'var(--card-bg)', borderColor: 'var(--card-border)' }}>
        {/* Collapse/expand toggle */}
        <button
          onClick={() => setLegendCollapsed(!legendCollapsed)}
          className="flex items-center gap-1 px-1 py-0.5 rounded transition-colors mr-1 flex-shrink-0"
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--hover-bg)')}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
          title={legendCollapsed ? 'Expand zone legend' : 'Collapse zone legend'}
        >
          <svg className={`w-3 h-3 transition-transform ${legendCollapsed ? '-rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: 'var(--text-muted)' }}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        {legendCollapsed ? (
          /* Collapsed: compact dot summary */
          <div className="flex items-center gap-1.5">
            {[1, 2, 3, 4, 5].map((zone) => (
              <button
                key={zone}
                onClick={() => toggleZone(zone)}
                className="flex items-center gap-1 px-1 py-0.5 rounded transition-all"
                style={{ opacity: visibleZones.includes(zone) ? 1 : 0.4 }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--hover-bg)')}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                title={`${visibleZones.includes(zone) ? 'Hide' : 'Show'} ${ZONE_CONFIG[zone].label} (${zoneCounts[zone] || 0})`}
              >
                <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: ZONE_CONFIG[zone].color }} />
                <span className="text-xs font-medium" style={{ color: 'var(--text-muted)' }}>{zoneCounts[zone] || 0}</span>
              </button>
            ))}
          </div>
        ) : (
          /* Expanded: full zone legend */
          <div className="flex items-center gap-3">
            {[1, 2, 3, 4, 5].map((zone) => {
              const isVisible = visibleZones.includes(zone);
              return (
                <button
                  key={zone}
                  onClick={() => toggleZone(zone)}
                  className={`flex items-center gap-2 whitespace-nowrap px-2 py-1 rounded transition-all ${
                    isVisible ? '' : 'opacity-40'
                  }`}
                  style={{ ['--tw-bg-opacity' as any]: undefined }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--hover-bg)')}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                  title={`${isVisible ? 'Hide' : 'Show'} ${ZONE_CONFIG[zone].label} (${ZONE_CONFIG[zone].risk})`}
                >
                  <div
                    className="w-3 h-3 rounded-full transition-opacity"
                    style={{ backgroundColor: ZONE_CONFIG[zone].color, opacity: isVisible ? 1 : 0.4 }}
                  />
                  <span className="text-xs" style={{ color: isVisible ? 'var(--text-secondary)' : 'var(--text-muted)', textDecoration: isVisible ? 'none' : 'line-through' }}>
                    {ZONE_CONFIG[zone].label}
                  </span>
                  <span className="text-sm font-semibold" style={{ color: isVisible ? 'var(--text-primary)' : 'var(--text-muted)' }}>
                    {zoneCounts[zone] || 0}
                  </span>
                </button>
              );
            })}
          </div>
        )}
        <div className="ml-auto pl-4 flex items-center gap-3 flex-shrink-0" style={{ borderLeft: '1px solid var(--card-border)' }}>
          {/* Molecule/patent counts */}
          <span className="text-sm whitespace-nowrap" style={{ color: 'var(--text-secondary)' }}>{totalMolecules} mol · {patentCount} patents</span>
          {/* Display limit slider for large files */}
          {showMoleculeSlider && (
            <div className="flex items-center gap-1.5">
              <span className="text-xs whitespace-nowrap" style={{ color: 'var(--text-muted)' }}>Showing:</span>
              <input
                type="range"
                min={minDisplay}
                max={maxDisplay}
                step="10"
                value={Math.min(displayLimit, maxDisplay)}
                onChange={(e) => setDisplayLimit(parseInt(e.target.value))}
                className="w-16 h-1 rounded-lg appearance-none cursor-pointer accent-blue-500"
                style={{ backgroundColor: 'var(--card-border)' }}
              />
              <span className="text-xs whitespace-nowrap" style={{ color: 'var(--text-primary)' }}>{Math.min(displayLimit, totalMolecules)} of {totalMolecules} molecules</span>
            </div>
          )}
          <div className="h-3 w-px flex-shrink-0" style={{ backgroundColor: 'var(--card-border)' }} />
          {/* At-Risk Only toggle */}
          <label className="flex items-center gap-2 cursor-pointer select-none whitespace-nowrap flex-shrink-0">
            <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>At-Risk Only</span>
            <button
              onClick={handleToggleAtRiskOnly}
              className={`relative w-8 h-[18px] rounded-full transition-colors ${atRiskOnly ? 'bg-red-500' : ''}`}
              style={atRiskOnly ? undefined : { backgroundColor: 'var(--card-border)' }}
            >
              <div className={`absolute top-[2px] w-[14px] h-[14px] rounded-full bg-white shadow transition-all ${atRiskOnly ? 'left-[16px]' : 'left-[2px]'}`} />
            </button>
          </label>
          {/* Export dropdown */}
          <div className="relative">
            <button onClick={() => setExportOpen(!exportOpen)} className="px-2.5 py-1 text-xs font-medium rounded transition-colors" style={{ backgroundColor: 'var(--hover-bg)', color: 'var(--text-primary)' }}>
              Export
            </button>
            {exportOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setExportOpen(false)} />
                <div className="absolute right-0 top-full mt-1 rounded-lg shadow-xl z-50 py-1 min-w-[140px]" style={{ backgroundColor: 'var(--dropdown-bg)', border: '1px solid var(--card-border)' }}>
                  <button onClick={() => { handleExportCSV(); setExportOpen(false); }} className="w-full text-left px-3 py-1.5 text-xs transition-colors" style={{ color: 'var(--text-primary)' }} onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--hover-bg)')} onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}>Export CSV</button>
                  <button onClick={() => { handleExportPDF(); setExportOpen(false); }} className="w-full text-left px-3 py-1.5 text-xs transition-colors" style={{ color: 'var(--text-primary)' }} onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--hover-bg)')} onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}>Export PDF</button>
                  <button onClick={() => { handleExportJSON(); setExportOpen(false); }} className="w-full text-left px-3 py-1.5 text-xs transition-colors" style={{ color: 'var(--text-primary)' }} onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--hover-bg)')} onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}>Export JSON</button>
                </div>
              </>
            )}
          </div>
          <div className="h-3 w-px flex-shrink-0" style={{ backgroundColor: 'var(--card-border)' }} />
          <span className="text-xs overflow-hidden text-ellipsis whitespace-nowrap" style={{ color: 'var(--text-muted)', maxWidth: '220px' }} title="SureChEMBL · 28.4M">SureChEMBL &middot; 28.4M</span>
        </div>
      </div>

      {/* Main area with background transition */}
      <div
        className={`flex flex-1 min-h-0 relative transition-opacity duration-300 ${isTransitioning ? 'opacity-0' : 'opacity-100'}`}
        style={{
          backgroundColor: (viewMode === 'overlap' || viewMode === 'jurisdictions') ? 'var(--card-bg)' : 'var(--content-bg)',
          transition: 'background-color 0.4s ease, opacity 0.3s',
        }}
      >
        {/* Floating pill view selector — centered above content */}
        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-30 flex items-center gap-0 px-0 py-0" style={{ background: 'var(--card-bg)', backdropFilter: 'blur(8px)', borderBottom: '2px solid var(--card-border)', borderRadius: 0 }}>
          {([
            { key: 'scatter' as ViewMode, label: '2D Scatter', icon: '\u2299', tooltip: '2D scatter plot — molecules plotted by zone and similarity' },
            { key: 'globe' as ViewMode, label: '3D Globe', icon: '\u25C9', tooltip: '3D globe — interactive spatial view of patent collisions' },
            { key: 'overlap' as ViewMode, label: 'Risk Report', icon: '\u25CE', tooltip: 'Risk report — portfolio IP risk summary and zone breakdown' },
            { key: 'jurisdictions' as ViewMode, label: 'Jurisdictions', icon: '\u25C8', tooltip: 'Jurisdictions — patent filings by country and region' },
          ]).map((btn) => (
            <button
              key={btn.key}
              onClick={() => handleViewChange(btn.key)}
              title={btn.tooltip}
              className="px-4 py-2 text-xs transition-all whitespace-nowrap"
              style={{
                background: viewMode === btn.key ? 'var(--card-bg)' : 'transparent',
                color: viewMode === btn.key ? '#14b8a6' : 'var(--text-secondary)',
                fontWeight: viewMode === btn.key ? 600 : 400,
                borderBottom: viewMode === btn.key ? '2px solid #14b8a6' : '2px solid transparent',
                marginBottom: '-2px',
              }}
            >
              <span className="mr-1">{btn.icon}</span>
              {btn.label}
            </button>
          ))}
          {/* Pop-out button for 3D Globe */}
          {viewMode === 'globe' && (
            <button
              onClick={handlePopoutPatentMap}
              title="Pop out 3D Globe to separate window"
              className="px-2 py-2 text-xs transition-all"
              style={{ color: 'var(--text-muted)', borderLeft: '1px solid var(--card-border)' }}
              onMouseEnter={(e) => (e.currentTarget.style.color = '#14b8a6')}
              onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--text-muted)')}
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13 5H1v14h14V7M11 3h10v10" />
              </svg>
            </button>
          )}
        </div>

        {/* ── RISK REPORT VIEW ── */}
        {viewMode === 'overlap' ? (
          <div className="flex-1 overflow-y-auto p-6 pt-16" style={{ backgroundColor: 'var(--card-bg)' }}>
            <div className="max-w-4xl mx-auto">

              {/* A) VERDICT CARD */}
              {(() => {
                const collisions = zoneCounts[1] + zoneCounts[2] + zoneCounts[3] + zoneCounts[4];
                const novel = zoneCounts[5] || 0;
                const pct = totalMolecules > 0 ? Math.round((collisions / totalMolecules) * 100) : 0;
                const isAllRisk = novel === 0 && totalMolecules > 0;
                const isMostlyRisk = pct > 50;
                return (
                  <div className={`rounded-xl p-6 mb-6 border ${isAllRisk ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800' : isMostlyRisk ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800' : 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'}`}>
                    <div className="flex items-start gap-4">
                      <span className="text-4xl">{isAllRisk ? '\u26A0\uFE0F' : isMostlyRisk ? '\u26A0\uFE0F' : '\u2705'}</span>
                      <div>
                        <h2 className={`text-xl font-bold mb-1 ${isAllRisk ? 'text-red-800 dark:text-red-300' : isMostlyRisk ? 'text-amber-800 dark:text-amber-300' : 'text-green-800 dark:text-green-300'}`}>
                          {isAllRisk ? `100% Patent Overlap` : `${100 - pct}% Novel Space`}
                        </h2>
                        <p className={`text-sm ${isAllRisk ? 'text-red-600 dark:text-red-400' : isMostlyRisk ? 'text-amber-600 dark:text-amber-400' : 'text-green-600 dark:text-green-400'}`}>
                          {isAllRisk
                            ? `All ${totalMolecules} molecules collide with known patent compounds. Structural modifications are recommended to find novel chemical space.`
                            : `${novel} of ${totalMolecules} molecules are outside known patent scope. ${isMostlyRisk ? 'Consider prioritising novel compounds for development.' : 'Good freedom-to-operate position for this portfolio.'}`
                          }
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* B) VENN DIAGRAM + NUMBERS */}
              {(() => {
                const collisionsB = zoneCounts[1] + zoneCounts[2] + zoneCounts[3] + zoneCounts[4];
                const novelB = zoneCounts[5] || 0;
                const overlapFrac = totalMolecules > 0 ? collisionsB / totalMolecules : 0;
                // Patent space is always large (r=110). Your Molecules scales with count.
                const patR = 110;
                const yourR = Math.max(40, Math.min(110, 40 + 70 * (totalMolecules / Math.max(totalMolecules, 1))));
                // Distance between centers controls overlap area proportionally:
                // If 100% collision → your circle fully inside patent circle (d = patR - yourR)
                // If 0% collision → circles don't overlap (d = patR + yourR)
                // Linear interpolation between these extremes
                const dNoOverlap = patR + yourR;
                const dFullOverlap = Math.max(0, patR - yourR);
                const d = dFullOverlap + (1 - overlapFrac) * (dNoOverlap - dFullOverlap);
                const patCx = 250 + d / 2;
                const yourCx = 250 - d / 2;
                return (
              <div className="flex items-center gap-8 mb-6">
                <svg viewBox="0 0 500 300" className="w-64 flex-shrink-0">
                  {/* Your Molecules circle */}
                  <circle cx={yourCx} cy={150} r={yourR} fill="rgba(220,38,38,0.12)" stroke="#dc2626" strokeWidth="2" />
                  <text x={yourCx} y={150 - yourR - 8} textAnchor="middle" fill="#dc2626" fontSize="13" fontWeight="600">Your Molecules</text>
                  {/* Patent Space circle */}
                  <circle cx={patCx} cy={150} r={patR} fill="rgba(59,130,246,0.12)" stroke="#3b82f6" strokeWidth="2" />
                  <text x={patCx} y={150 - patR - 8} textAnchor="middle" fill="#3b82f6" fontSize="13" fontWeight="600">Patent Space</text>
                  {/* Overlap (clipped intersection) */}
                  <clipPath id="leftClipRisk"><circle cx={yourCx} cy={150} r={yourR} /></clipPath>
                  <circle cx={patCx} cy={150} r={patR} fill="rgba(245,158,11,0.25)" clipPath="url(#leftClipRisk)" />
                  {/* Collision count in overlap center */}
                  {collisionsB > 0 && (
                    <>
                      <text x={(yourCx + patCx) / 2} y={155} textAnchor="middle" fill="#d97706" fontSize="28" fontWeight="700">
                        {collisionsB}
                      </text>
                      <text x={(yourCx + patCx) / 2} y={175} textAnchor="middle" fill="#92400e" fontSize="10">collisions</text>
                    </>
                  )}
                  {/* Novel count in your-only area */}
                  {novelB > 0 && overlapFrac < 1 && (
                    <>
                      <text x={yourCx - yourR * 0.35} y={155} textAnchor="middle" fill="#16a34a" fontSize="20" fontWeight="700">
                        {novelB}
                      </text>
                      <text x={yourCx - yourR * 0.35} y={172} textAnchor="middle" fill="#16a34a" fontSize="9">novel</text>
                    </>
                  )}
                </svg>
                <div className="flex gap-8">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-red-500">{totalMolecules}</div>
                    <div className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>Your Molecules</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-amber-500">{zoneCounts[1] + zoneCounts[2] + zoneCounts[3] + zoneCounts[4]}</div>
                    <div className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>Collisions</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-green-500">{zoneCounts[5] || 0}</div>
                    <div className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>Novel</div>
                  </div>
                </div>
              </div>
                );
              })()}

              {/* C) ZONE BREAKDOWN CARDS */}
              <h3 className="text-sm font-semibold uppercase tracking-wide mb-3" style={{ color: 'var(--text-secondary)' }}>Zone Breakdown</h3>
              <div className="grid grid-cols-2 gap-3 mb-6">
                {[1, 2, 3, 4, 5].map((zone) => {
                  const zc = ZONE_CONFIG[zone];
                  const count = zoneCounts[zone] || 0;
                  const riskText = zone <= 2 ? 'Potential infringement' : zone === 3 ? 'Review recommended' : zone === 4 ? 'Likely safe' : 'No patent overlap';
                  return (
                    <div key={zone} className="flex items-center gap-3 p-3 rounded-lg" style={{ backgroundColor: 'var(--content-bg)', border: '1px solid var(--card-border)' }}>
                      <div className="flex items-center justify-center w-10 h-10 rounded-lg text-white text-sm font-bold" style={{ backgroundColor: zc.color }}>
                        {count}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{zc.label}</div>
                        <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>Zone {zone} &middot; {riskText}</div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* D) SIMILARITY METRIC TOGGLE + PATENT HITS TABLE */}
              <div className="flex items-center gap-3 mb-4">
                <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-secondary)' }}>
                  Similarity Metric
                </span>
                <div className="flex rounded-lg overflow-hidden" style={{ border: '1px solid var(--card-border)' }}>
                  {([
                    { key: 'tanimoto' as SimilarityViewMode, label: 'Overall Similarity', sub: 'Tanimoto' },
                    { key: 'mcs' as SimilarityViewMode, label: 'Atoms to Change', sub: 'MCS' },
                    { key: 'substructure' as SimilarityViewMode, label: 'Shared Scaffold', sub: 'Substructure' },
                  ] as const).map(btn => (
                    <button
                      key={btn.key}
                      onClick={() => {
                        setSimilarityViewMode(btn.key);
                        if (btn.key !== 'tanimoto') fetchSimilarityData(btn.key);
                      }}
                      disabled={similarityLoading}
                      className="px-3 py-1.5 text-xs"
                      style={{
                        background: similarityViewMode === btn.key ? '#14b8a6' : 'var(--card-bg)',
                        color: similarityViewMode === btn.key ? 'white' : 'var(--text-secondary)',
                        fontWeight: similarityViewMode === btn.key ? 600 : 400,
                        borderRight: '1px solid var(--card-border)',
                      }}
                    >
                      {btn.label}
                    </button>
                  ))}
                </div>
                {similarityLoading && (
                  <span className="text-xs animate-pulse" style={{ color: 'var(--text-muted)' }}>Computing...</span>
                )}
              </div>

              {(() => {
                const allHits: { molName: string; molId: number; molSmiles: string; surechemblId: string; similarity: number; zone: number; patentFamily: string; status: string; cacheKey: string }[] = [];
                molecules.forEach(mol => {
                  const matches = mol.top_patent_matches || mol.patent_matches || [];
                  matches.forEach(pm => {
                    const sim = (pm.similarity || 0) * 100;
                    const isExact = pm.match_type === 'exact' || sim >= 99;
                    allHits.push({
                      molName: mol.name || `Mol ${mol.id}`,
                      molId: mol.id,
                      molSmiles: mol.smiles,
                      surechemblId: pm.surechembl_id || pm.patent_number || 'Unknown',
                      similarity: sim,
                      zone: mol.zone,
                      patentFamily: pm.patent_title || pm.patent_number || '\u2014',
                      status: isExact ? `Exact (Tanimoto 1.0)` : `Similar (Tanimoto ${(sim / 100).toFixed(2)})`,
                      cacheKey: `${mol.smiles}||${pm.surechembl_id || pm.patent_number}`,
                    });
                  });
                });

                // Sort based on active similarity mode
                if (similarityViewMode === 'mcs') {
                  allHits.sort((a, b) => (mcsCache.get(b.cacheKey)?.mcs_fraction ?? 0) - (mcsCache.get(a.cacheKey)?.mcs_fraction ?? 0));
                } else if (similarityViewMode === 'substructure') {
                  allHits.sort((a, b) => {
                    const am = substructureCache.get(a.cacheKey)?.is_match ? 1 : 0;
                    const bm = substructureCache.get(b.cacheKey)?.is_match ? 1 : 0;
                    return bm - am || (substructureCache.get(b.cacheKey)?.matching_atoms ?? 0) - (substructureCache.get(a.cacheKey)?.matching_atoms ?? 0);
                  });
                } else {
                  allHits.sort((a, b) => b.similarity - a.similarity);
                }

                return allHits.length > 0 ? (
                  <>
                    <h3 className="text-sm font-semibold uppercase tracking-wide mb-1" style={{ color: 'var(--text-secondary)' }}>Patent Hits ({allHits.length})</h3>
                    <p className="text-xs mb-3" style={{ color: 'var(--text-muted)' }}>Matched against 28.4M patent compounds (SureChEMBL)</p>
                    <div className="rounded-lg overflow-hidden mb-6" style={{ border: '1px solid var(--card-border)' }}>
                      <table className="w-full text-sm">
                        <thead>
                          <tr style={{ backgroundColor: 'var(--content-bg)', borderBottom: '1px solid var(--card-border)' }}>
                            <th className="text-left px-3 py-2 text-xs font-semibold" style={{ color: 'var(--text-secondary)' }}>Compound</th>
                            {similarityViewMode === 'tanimoto' && (
                              <>
                                <th className="text-left px-3 py-2 text-xs font-semibold" style={{ color: 'var(--text-secondary)' }}>Similarity</th>
                                <th className="text-left px-3 py-2 text-xs font-semibold" style={{ color: 'var(--text-secondary)' }}>Zone</th>
                                <th className="text-left px-3 py-2 text-xs font-semibold" style={{ color: 'var(--text-secondary)' }}>Patent / Family</th>
                                <th className="text-left px-3 py-2 text-xs font-semibold" style={{ color: 'var(--text-secondary)' }}>Status</th>
                              </>
                            )}
                            {similarityViewMode === 'mcs' && (
                              <>
                                <th className="text-left px-3 py-2 text-xs font-semibold" style={{ color: 'var(--text-secondary)' }}>Shared Atoms</th>
                                <th className="text-left px-3 py-2 text-xs font-semibold" style={{ color: 'var(--text-secondary)' }}>Atoms to Change</th>
                                <th className="text-left px-3 py-2 text-xs font-semibold" style={{ color: 'var(--text-secondary)' }}>MCS Fraction</th>
                                <th className="text-left px-3 py-2 text-xs font-semibold" style={{ color: 'var(--text-secondary)' }}>Patent / Family</th>
                              </>
                            )}
                            {similarityViewMode === 'substructure' && (
                              <>
                                <th className="text-left px-3 py-2 text-xs font-semibold" style={{ color: 'var(--text-secondary)' }}>Match</th>
                                <th className="text-left px-3 py-2 text-xs font-semibold" style={{ color: 'var(--text-secondary)' }}>Shared Atoms</th>
                                <th className="text-left px-3 py-2 text-xs font-semibold" style={{ color: 'var(--text-secondary)' }}>Direction</th>
                                <th className="text-left px-3 py-2 text-xs font-semibold" style={{ color: 'var(--text-secondary)' }}>Patent / Family</th>
                              </>
                            )}
                          </tr>
                        </thead>
                        <tbody>
                          {allHits.slice(0, 50).map((hit, i) => {
                            const mcs = mcsCache.get(hit.cacheKey);
                            const sub = substructureCache.get(hit.cacheKey);
                            return (
                            <tr key={i} style={{ borderBottom: '1px solid var(--card-border)', backgroundColor: i % 2 === 0 ? 'var(--card-bg)' : 'var(--content-bg)' }}>
                              <td className="px-3 py-2">
                                <div className="font-medium text-xs" style={{ color: 'var(--text-primary)' }}>{hit.molName}</div>
                                <button
                                  onClick={() => setCompoundDetail({
                                    id: hit.surechemblId,
                                    similarity: hit.similarity / 100,
                                    zone: hit.zone,
                                    patent: hit.patentFamily,
                                  })}
                                  className="text-xs hover:brightness-125"
                                  style={{ color: '#2563eb', textDecoration: 'underline', cursor: 'pointer', background: 'none', border: 'none', padding: 0, font: 'inherit' }}
                                >
                                  {hit.surechemblId}
                                </button>
                              </td>

                              {/* Tanimoto columns */}
                              {similarityViewMode === 'tanimoto' && (
                                <>
                                  <td className="px-3 py-2">
                                    <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold text-white ${
                                      hit.similarity >= 95 ? 'bg-red-500' : hit.similarity >= 80 ? 'bg-amber-500' : 'bg-green-500'
                                    }`}>
                                      {hit.similarity.toFixed(1)}%
                                    </span>
                                  </td>
                                  <td className="px-3 py-2">
                                    <span className="inline-flex items-center gap-1.5 text-xs" style={{ color: 'var(--text-primary)' }}>
                                      <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: ZONE_CONFIG[hit.zone]?.color }} />
                                      {ZONE_CONFIG[hit.zone]?.label}
                                    </span>
                                  </td>
                                  <td className="px-3 py-2 text-xs max-w-[200px] truncate" style={{ color: 'var(--text-secondary)' }}>{hit.patentFamily}</td>
                                  <td className="px-3 py-2 text-xs" style={{ color: 'var(--text-secondary)' }}>{hit.status}</td>
                                </>
                              )}

                              {/* MCS columns */}
                              {similarityViewMode === 'mcs' && (
                                <>
                                  <td className="px-3 py-2 text-xs" style={{ color: 'var(--text-primary)' }}>
                                    {mcs && !mcs.error ? `${mcs.mcs_atoms} / ${Math.max(mcs.mol1_atoms, mcs.mol2_atoms)}` : '\u2014'}
                                  </td>
                                  <td className="px-3 py-2">
                                    {mcs && !mcs.error ? (
                                      <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold text-white ${
                                        mcs.transform_distance <= 3 ? 'bg-red-500' : mcs.transform_distance <= 8 ? 'bg-amber-500' : 'bg-green-500'
                                      }`}>
                                        {mcs.transform_distance} atoms
                                      </span>
                                    ) : '\u2014'}
                                  </td>
                                  <td className="px-3 py-2">
                                    {mcs && !mcs.error ? (
                                      <div className="flex items-center gap-2">
                                        <div className="flex-1 h-1.5 rounded-full" style={{ backgroundColor: 'var(--card-border)', maxWidth: 60 }}>
                                          <div className="h-1.5 rounded-full" style={{
                                            width: `${Math.round(mcs.mcs_fraction * 100)}%`,
                                            backgroundColor: mcs.mcs_fraction >= 0.8 ? '#ef4444' : mcs.mcs_fraction >= 0.5 ? '#f59e0b' : '#22c55e',
                                          }} />
                                        </div>
                                        <span className="text-xs" style={{ color: 'var(--text-primary)' }}>{Math.round(mcs.mcs_fraction * 100)}%</span>
                                      </div>
                                    ) : '\u2014'}
                                  </td>
                                  <td className="px-3 py-2 text-xs max-w-[200px] truncate" style={{ color: 'var(--text-secondary)' }}>{hit.patentFamily}</td>
                                </>
                              )}

                              {/* Substructure columns */}
                              {similarityViewMode === 'substructure' && (
                                <>
                                  <td className="px-3 py-2 text-center">
                                    {sub ? (
                                      sub.is_match
                                        ? <span className="text-red-500 font-bold text-sm" title="Substructure match found">Yes</span>
                                        : <span className="text-green-500 font-bold text-sm" title="No substructure match">No</span>
                                    ) : '\u2014'}
                                  </td>
                                  <td className="px-3 py-2 text-xs" style={{ color: 'var(--text-primary)' }}>
                                    {sub && !sub.error ? `${sub.matching_atoms} / ${Math.max(sub.query_atoms, sub.target_atoms)}` : '\u2014'}
                                  </td>
                                  <td className="px-3 py-2 text-xs" style={{ color: 'var(--text-secondary)' }}>
                                    {sub?.direction === 'query_in_target' ? 'Your mol in patent'
                                      : sub?.direction === 'target_in_query' ? 'Patent in your mol'
                                      : sub ? 'None' : '\u2014'}
                                  </td>
                                  <td className="px-3 py-2 text-xs max-w-[200px] truncate" style={{ color: 'var(--text-secondary)' }}>{hit.patentFamily}</td>
                                </>
                              )}
                            </tr>
                            );
                          })}
                        </tbody>
                      </table>
                      {allHits.length > 50 && (
                        <div className="px-3 py-2 text-xs text-center" style={{ backgroundColor: 'var(--content-bg)', color: 'var(--text-secondary)', borderTop: '1px solid var(--card-border)' }}>
                          Showing 50 of {allHits.length} patent hits. Export CSV for full data.
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="p-4 rounded-lg text-sm text-center mb-6" style={{ backgroundColor: 'var(--content-bg)', border: '1px solid var(--card-border)', color: 'var(--text-secondary)' }}>
                    No patent matches found. Run Intelligence to scan for patent conflicts.
                  </div>
                );
              })()}

              {/* E) RECOMMENDATION */}
              {(() => {
                const collisions = zoneCounts[1] + zoneCounts[2] + zoneCounts[3] + zoneCounts[4];
                const novel = zoneCounts[5] || 0;
                const lines: string[] = [];
                if (collisions === totalMolecules && totalMolecules > 0) {
                  lines.push(`All ${totalMolecules} molecules show patent overlap. Consider structural modifications to explore novel chemical space.`);
                }
                if (zoneCounts[1] > 0) {
                  lines.push(`${zoneCounts[1]} Literal Match${zoneCounts[1] !== 1 ? 'es' : ''} (Zone 1) require${zoneCounts[1] === 1 ? 's' : ''} immediate attention — these are exact matches found in SureChEMBL patent literature.`);
                }
                if (zoneCounts[2] > 0) {
                  lines.push(`${zoneCounts[2]} Chemical Isostere${zoneCounts[2] !== 1 ? 's' : ''} (Zone 2) — halogen/methyl substitutions that likely fall within patent scope.`);
                }
                if (zoneCounts[3] > 0) {
                  lines.push(`${zoneCounts[3]} Biological Isostere hit${zoneCounts[3] !== 1 ? 's' : ''} (Zone 3) have room for optimisation — functional group replacements may differentiate from patent claims.`);
                }
                if (zoneCounts[4] > 0) {
                  lines.push(`${zoneCounts[4]} Close Analog${zoneCounts[4] !== 1 ? 's' : ''} (Zone 4) share structural scaffolds with patented compounds but may have sufficient novelty.`);
                }
                if (novel > 0) {
                  lines.push(`${novel} molecule${novel !== 1 ? 's are' : ' is'} in novel chemical space — no patent concerns detected.`);
                }
                return lines.length > 0 ? (
                  <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded-xl p-5">
                    <h3 className="text-sm font-semibold text-amber-800 dark:text-amber-300 mb-2">Recommendations</h3>
                    <ul className="space-y-1.5">
                      {lines.map((line, i) => (
                        <li key={i} className="text-sm text-amber-700 dark:text-amber-400 flex gap-2">
                          <span className="text-amber-400 dark:text-amber-500 mt-0.5">&#x2022;</span>
                          <span>{line}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null;
              })()}

            </div>
          </div>

        ) : viewMode === 'jurisdictions' ? (
          /* ── JURISDICTIONS VIEW ── */
          <div className="flex-1 overflow-y-auto p-6 pt-16" style={{ backgroundColor: 'var(--card-bg)' }}>
            <div className="max-w-3xl mx-auto">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>Patent Jurisdictions</h2>
                <div className="flex items-center gap-0.5 rounded-lg p-0.5" style={{ backgroundColor: 'var(--content-bg)' }}>
                  <button
                    onClick={() => setJurisdictionsSubMode('list')}
                    className="px-3 py-1 text-xs rounded-md font-medium transition-colors"
                    style={jurisdictionsSubMode === 'list' ? { backgroundColor: 'var(--card-bg)', color: 'var(--text-primary)', boxShadow: '0 1px 2px rgba(0,0,0,0.1)' } : { color: 'var(--text-secondary)' }}
                  >
                    List
                  </button>
                  <button
                    onClick={() => setJurisdictionsSubMode('map')}
                    className="px-3 py-1 text-xs rounded-md font-medium transition-colors"
                    style={jurisdictionsSubMode === 'map' ? { backgroundColor: 'var(--card-bg)', color: 'var(--text-primary)', boxShadow: '0 1px 2px rgba(0,0,0,0.1)' } : { color: 'var(--text-secondary)' }}
                  >
                    Map
                  </button>
                </div>
              </div>

              {jurisdictionsSubMode === 'list' ? (
                <div className="space-y-2">
                  {(() => {
                    // Use real data if available, fallback to dummy
                    const jData = mapData?.jurisdictions && Object.keys(mapData.jurisdictions).length > 0
                      ? Object.entries(mapData.jurisdictions).sort((a, b) => b[1] - a[1])
                      : [['US', 47], ['EP', 31], ['JP', 22], ['CN', 18], ['KR', 8], ['AU', 5], ['CA', 4], ['IN', 3]] as [string, number][];
                    const maxCount = jData.length > 0 ? (jData[0][1] as number) : 1;
                    const FLAG_EMOJI: Record<string, string> = { US: '\uD83C\uDDFA\uD83C\uDDF8', EP: '\uD83C\uDDEA\uD83C\uDDFA', JP: '\uD83C\uDDEF\uD83C\uDDF5', CN: '\uD83C\uDDE8\uD83C\uDDF3', KR: '\uD83C\uDDF0\uD83C\uDDF7', AU: '\uD83C\uDDE6\uD83C\uDDFA', CA: '\uD83C\uDDE8\uD83C\uDDE6', IN: '\uD83C\uDDEE\uD83C\uDDF3', GB: '\uD83C\uDDEC\uD83C\uDDE7', DE: '\uD83C\uDDE9\uD83C\uDDEA', FR: '\uD83C\uDDEB\uD83C\uDDF7', BR: '\uD83C\uDDE7\uD83C\uDDF7', USPTO: '\uD83C\uDDFA\uD83C\uDDF8', EPO: '\uD83C\uDDEA\uD83C\uDDFA', JPO: '\uD83C\uDDEF\uD83C\uDDF5', CNIPA: '\uD83C\uDDE8\uD83C\uDDF3', KIPO: '\uD83C\uDDF0\uD83C\uDDF7', WIPO: '\uD83C\uDFF3\uFE0F' };
                    return jData.map(([code, count], idx) => {
                      const c = count as number;
                      const pct = (c / maxCount) * 100;
                      const severity = pct > 60 ? '#dc2626' : pct > 30 ? '#f97316' : '#eab308';
                      return (
                        <div key={code} className="flex items-center gap-3 rounded-lg px-4 py-3" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--card-border)', opacity: 0, animation: `fadeSlideIn 0.3s ease forwards`, animationDelay: `${idx * 50}ms` }}>
                          <span className="text-lg">{FLAG_EMOJI[code as string] || '\uD83C\uDFF3\uFE0F'}</span>
                          <span className="text-sm font-medium w-24" style={{ color: 'var(--text-primary)' }}>{JURISDICTION_NAMES[code as string] || code}</span>
                          <span className="text-xs w-8" style={{ color: 'var(--text-muted)' }}>{code}</span>
                          <div className="flex-1 h-5 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--bar-track)' }}>
                            <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: severity }} />
                          </div>
                          <span className="text-sm font-semibold w-10 text-right" style={{ color: 'var(--text-primary)' }}>{c}</span>
                        </div>
                      );
                    });
                  })()}
                </div>
              ) : (
                /* Map sub-view */
                <div className="rounded-xl overflow-hidden" style={{ height: 400, backgroundColor: 'var(--content-bg)', border: '1px solid var(--card-border)', position: 'relative' }}>
                  <ComposableMap
                    projection="geoMercator"
                    projectionConfig={{ scale: 120, center: [10, 20] }}
                    width={800}
                    height={400}
                    style={{ width: '100%', height: '100%', display: 'block' }}
                  >
                    <Geographies geography={GEO_URL}>
                      {({ geographies }: { geographies: any[] }) => {
                        if (!geographies || geographies.length === 0) {
                          return (
                            <text x={400} y={200} textAnchor="middle" fill="var(--text-muted, #64748b)" fontSize={14}>
                              Loading map data...
                            </text>
                          );
                        }
                        const jur = mapData?.jurisdictions && Object.keys(mapData.jurisdictions).length > 0
                          ? mapData.jurisdictions
                          : { US: 47, EP: 31, JP: 22, CN: 18, KR: 8, AU: 5, CA: 4, IN: 3 } as Record<string, number>;
                        // Build per-country: highest-risk zone + total count
                        const countryInfo = new Map<string, { zone: number; count: number; codes: string[] }>();
                        geographies.forEach((geo: any) => {
                          const countryName = geo.properties.name;
                          const codes = COUNTRY_TO_JURISDICTION[countryName] || [];
                          let total = 0;
                          let bestZone = 5;
                          codes.forEach(code => {
                            total += (jur[code] || 0);
                            const z = getJurisdictionZone(code);
                            if (z < bestZone) bestZone = z;
                          });
                          if (total > 0) countryInfo.set(countryName, { zone: bestZone, count: total, codes });
                        });
                        return geographies.map((geo: any) => {
                          const countryName = geo.properties.name;
                          const info = countryInfo.get(countryName);
                          const zoneColor = info ? ZONE_CONFIG[info.zone]?.color : null;
                          // Build tooltip with zone breakdown
                          const tooltipLines: string[] = [];
                          if (info) {
                            tooltipLines.push(`${countryName}: ${info.count} patents`);
                            info.codes.forEach(code => {
                              const zones = jurisdictionZones[code];
                              if (zones) {
                                for (let z = 1; z <= 5; z++) {
                                  if (zones[z] > 0) tooltipLines.push(`  ${ZONE_CONFIG[z]?.label}: ${zones[z]}`);
                                }
                              }
                            });
                          }
                          return (
                            <Geography
                              key={geo.rsmKey}
                              geography={geo}
                              fill={zoneColor || '#94a3b8'}
                              stroke="#64748b"
                              strokeWidth={0.5}
                              style={{ default: { outline: 'none' }, hover: { fill: info ? '#e2e8f0' : '#cbd5e1', outline: 'none', cursor: info ? 'pointer' : 'default' }, pressed: { outline: 'none' } }}
                            >
                              {info && <title>{tooltipLines.join('\n')}</title>}
                            </Geography>
                          );
                        });
                      }}
                    </Geographies>
                  </ComposableMap>
                  {/* Pulsing dots at patent office locations — SVG overlay */}
                  <svg viewBox="0 0 800 400" style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none' }}>
                    {(() => {
                      const jur = mapData?.jurisdictions && Object.keys(mapData.jurisdictions).length > 0
                        ? mapData.jurisdictions
                        : { US: 47, EP: 31, JP: 22, CN: 18, KR: 8, AU: 5, CA: 4, IN: 3 } as Record<string, number>;
                      // Top 8 jurisdictions by count — avoids clutter
                      const topCodes = Object.entries(jur)
                        .filter(([code]) => OFFICE_LOCATIONS[code as keyof typeof OFFICE_LOCATIONS])
                        .sort((a, b) => b[1] - a[1])
                        .slice(0, 8)
                        .map(([code]) => code);
                      return topCodes.map(code => {
                        const loc = OFFICE_LOCATIONS[code as keyof typeof OFFICE_LOCATIONS];
                        const count = jur[code] || 0;
                        if (count === 0 || !loc) return null;
                        const r = Math.max(6, Math.min(18, 4 + 3 * Math.log10(count + 1)));
                        const dotZone = getJurisdictionZone(code);
                        const dotColor = ZONE_CONFIG[dotZone]?.color || '#22c55e';
                        const labelY = loc.cy + r + 12;
                        const labelText = `${code}: ${count.toLocaleString()}`;
                        const labelWidth = labelText.length * 5.5 + 8;
                        return (
                          <g key={code}>
                            <circle cx={loc.cx} cy={loc.cy} r={r * 1.8} fill={dotColor} opacity={0.15} style={{ animation: 'jurisdictionPulse 2s ease-in-out infinite', transformOrigin: `${loc.cx}px ${loc.cy}px` }} />
                            <circle cx={loc.cx} cy={loc.cy} r={r} fill={dotColor} opacity={0.85} stroke="#fff" strokeWidth={1.5} style={{ animation: 'jurisdictionPulse 2s ease-in-out infinite', transformOrigin: `${loc.cx}px ${loc.cy}px`, pointerEvents: 'auto', cursor: 'pointer' }}>
                              <title>{`${loc.name}: ${count.toLocaleString()} patents`}</title>
                            </circle>
                            {/* Label with background for contrast */}
                            <rect x={loc.cx - labelWidth / 2} y={labelY - 8} width={labelWidth} height={14} rx={3} fill={isDarkMode ? 'rgba(15,23,42,0.75)' : 'rgba(255,255,255,0.8)'} />
                            <text x={loc.cx} y={labelY + 1} textAnchor="middle" fontSize={10} fontWeight={600} fill={isDarkMode ? '#e2e8f0' : '#1e293b'}>{labelText}</text>
                          </g>
                        );
                      });
                    })()}
                  </svg>
                  {/* Legend */}
                  <div style={{ position: 'absolute', bottom: 12, right: 12, display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, color: 'var(--text-muted)' }}>
                    <span>Low</span>
                    <div style={{ display: 'flex', gap: 2 }}>
                      {['#f87171', '#ef4444', '#dc2626', '#991b1b'].map((c) => (
                        <div key={c} style={{ width: 16, height: 10, backgroundColor: c, borderRadius: 2 }} />
                      ))}
                    </div>
                    <span>High</span>
                  </div>
                  {/* Dot legend */}
                  <div style={{ position: 'absolute', bottom: 12, left: 12, display: 'flex', alignItems: 'center', gap: 10, fontSize: 11, color: 'var(--text-muted)' }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}><span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#dc2626', display: 'inline-block' }} />&gt;1k</span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}><span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#f59e0b', display: 'inline-block' }} />&gt;100</span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}><span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#22c55e', display: 'inline-block' }} />&le;100</span>
                  </div>
                </div>
              )}
            </div>
          </div>

        ) : viewMode === 'globe' && mapData ? (
          /* ── 3D GLOBE VIEW — lazy-loaded, unmounts when switching away ── */
          <div className="flex-1 relative min-h-0">
            <Suspense fallback={
              <div className="flex-1 flex items-center justify-center h-full">
                <div className="text-center">
                  <div className="w-8 h-8 border-2 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                  <span className="text-sm" style={{ color: 'var(--text-muted)' }}>Loading 3D Globe...</span>
                </div>
              </div>
            }>
              <PatentCollisionMap3D
                mapData={mapData}
                onMoleculeSelect={(mol) => {
                  setSelectedMolecule(mol);
                  setSelectedPatent(null);
                  // Store for grid scroll when switching back
                  if (mol?.id) setPendingScrollMoleculeId(mol.id);
                  // Broadcast to popout window
                  patentMapChannelRef.current?.postMessage({ type: 'select-molecule', molecule: mol });
                }}
                selectedMolecule={selectedMolecule}
                pinnedMolecules={pinnedMolecules}
                onPinnedChange={setPinnedMolecules}
                collisionViewMode={collisionViewMode}
                visibleZones={visibleZones}
                displayLimit={displayLimit}
              />
            </Suspense>
            {/* Collision View Mode Toggle — overlaid on 3D globe */}
            <div style={{
              position: 'absolute', top: 12, left: 50, zIndex: 20,
              display: 'flex', gap: 0, borderRadius: 8,
              overflow: 'hidden', border: '1px solid rgba(255,255,255,0.2)',
              fontSize: 12, fontFamily: 'inherit',
              boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
              pointerEvents: 'auto',
            }}>
              <button
                onClick={() => setCollisionViewMode('my-molecules')}
                style={{
                  padding: '6px 14px',
                  background: collisionViewMode === 'my-molecules' ? 'rgba(20,184,166,0.8)' : 'rgba(0,0,0,0.4)',
                  color: '#fff', border: 'none', cursor: 'pointer',
                  fontWeight: collisionViewMode === 'my-molecules' ? 600 : 400,
                  transition: 'background 0.2s',
                }}
              >
                My Molecules → Patents
              </button>
              <button
                onClick={() => setCollisionViewMode('patent-landscape')}
                style={{
                  padding: '6px 14px',
                  background: collisionViewMode === 'patent-landscape' ? 'rgba(20,184,166,0.8)' : 'rgba(0,0,0,0.4)',
                  color: '#fff', border: 'none', cursor: 'pointer',
                  fontWeight: collisionViewMode === 'patent-landscape' ? 600 : 400,
                  transition: 'background 0.2s',
                }}
              >
                Patent Landscape
              </button>
            </div>
            {/* Contextual hint for patent landscape mode on globe */}
            {collisionViewMode === 'patent-landscape' && (
              <div style={{
                position: 'absolute', top: 44, left: 50, zIndex: 20,
                fontSize: 11, color: 'rgba(255,255,255,0.55)',
                maxWidth: 280, lineHeight: '1.4',
                fontFamily: 'inherit',
                pointerEvents: 'none',
              }}>
                Patent families sized by claim breadth. Your molecules shown as small coloured markers. Distance = chemical dissimilarity.
              </div>
            )}

            {/* Globe handles its own pins internally — no parent overlay */}
          </div>

        ) : viewMode === 'scatter' ? (
          /* ── 2D SCATTER VIEW — canvas + right panel ── */
            <div className="flex-1 relative overflow-hidden pt-12" ref={containerRef}
              onMouseDown={handleScatterMouseDown}
              onMouseMove={handleScatterMouseMove}
              onMouseUp={handleScatterMouseUp}
              onMouseLeave={handleScatterMouseUp}
            >
              <canvas
                ref={canvasRef}
                onClick={handleCanvasClick}
                onMouseMove={handleCanvasMove}
                className="w-full h-full cursor-crosshair"
                style={{
                  transform: `translate(${panOffsetX}px, ${panOffsetY}px) scale(${zoomLevel})`,
                  transformOrigin: 'top left',
                  transition: isPanning ? 'none' : 'transform 0.12s ease-out',
                }}
              />

              {/* Collision View Mode Toggle */}
              <div style={{
                position: 'absolute', top: 12, left: 12, zIndex: 20,
                display: 'flex', gap: 0, borderRadius: 8,
                overflow: 'hidden', border: '1px solid rgba(255,255,255,0.2)',
                fontSize: 12, fontFamily: 'inherit',
                boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
              }}>
                <button
                  onClick={() => setCollisionViewMode('my-molecules')}
                  style={{
                    padding: '6px 14px',
                    background: collisionViewMode === 'my-molecules' ? 'rgba(20,184,166,0.8)' : 'rgba(0,0,0,0.4)',
                    color: '#fff', border: 'none', cursor: 'pointer',
                    fontWeight: collisionViewMode === 'my-molecules' ? 600 : 400,
                    transition: 'background 0.2s',
                  }}
                >
                  My Molecules → Patents
                </button>
                <button
                  onClick={() => setCollisionViewMode('patent-landscape')}
                  style={{
                    padding: '6px 14px',
                    background: collisionViewMode === 'patent-landscape' ? 'rgba(20,184,166,0.8)' : 'rgba(0,0,0,0.4)',
                    color: '#fff', border: 'none', cursor: 'pointer',
                    fontWeight: collisionViewMode === 'patent-landscape' ? 600 : 400,
                    transition: 'background 0.2s',
                  }}
                >
                  Patent Landscape
                </button>
              </div>
              {/* Contextual hint for active mode */}
              {collisionViewMode === 'patent-landscape' && (
                <div style={{
                  position: 'absolute', top: 44, left: 12, zIndex: 20,
                  fontSize: 11, color: 'rgba(255,255,255,0.55)',
                  maxWidth: 280, lineHeight: '1.4',
                  fontFamily: 'inherit',
                }}>
                  Patent families sized by claim breadth. Your molecules shown as small coloured markers. Distance = chemical dissimilarity.
                </div>
              )}

              {/* Zoom Controls */}
              <div className="absolute bottom-4 right-4 flex flex-col gap-1 rounded-lg p-1 shadow-lg z-20" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--card-border)' }}>
                <button
                  onClick={handleZoomIn}
                  className="w-8 h-8 flex items-center justify-center rounded transition-colors text-lg font-semibold"
                  style={{ color: 'var(--text-primary)' }}
                  title="Zoom in (20%)"
                >
                  +
                </button>
                <button
                  onClick={handleZoomReset}
                  className="w-8 h-8 flex items-center justify-center rounded transition-colors text-xs font-medium"
                  style={{ color: 'var(--text-secondary)' }}
                  title="Reset zoom"
                >
                  {Math.round(zoomLevel * 100)}%
                </button>
                <button
                  onClick={handleZoomOut}
                  className="w-8 h-8 flex items-center justify-center rounded transition-colors text-lg font-semibold"
                  style={{ color: 'var(--text-primary)' }}
                  title="Zoom out (20%)"
                >
                  −
                </button>
              </div>

              {/* Tooltip */}
              {hoveredItem && containerRef.current && animationPhase === 'complete' && (
                <div
                  className="absolute pointer-events-none rounded-lg px-3 py-2 shadow-xl z-10"
                  style={{
                    backgroundColor: 'var(--card-bg)',
                    border: '1px solid var(--card-border)',
                    left: Math.min(
                      (positions[`mol-${hoveredItem.id}`]?.x || 0) * (containerRef.current.clientWidth / 800) + 15,
                      containerRef.current.clientWidth - 200
                    ),
                    top: Math.max(
                      (positions[`mol-${hoveredItem.id}`]?.y || 0) * (containerRef.current.clientHeight / 600) - 30,
                      10
                    ),
                  }}
                >
                  <div className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                    {hoveredItem.data.name || `Molecule ${hoveredItem.id}`}
                  </div>
                  <div className="text-xs mt-1" style={{ color: ZONE_CONFIG[hoveredItem.data.zone]?.color }}>
                    {ZONE_CONFIG[hoveredItem.data.zone]?.label}
                    {hoveredItem.data.patent_count_exact > 0 && ` • ${hoveredItem.data.patent_count_exact} exact`}
                    {hoveredItem.data.patent_count_similar > 0 && ` • ${hoveredItem.data.patent_count_similar} similar`}
                  </div>
                </div>
              )}

              {/* SVG connecting lines — pinned structures to data points */}
              {animationPhase === 'complete' && pinnedMolecules.size > 0 && containerRef.current && (
                <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 14, overflow: 'visible' }}>
                  {Array.from(pinnedMolecules.values()).map(mol => {
                    const pos = positions[`mol-${mol.id}`];
                    if (!pos || !containerRef.current) return null;
                    const cw = containerRef.current.clientWidth;
                    const ch = containerRef.current.clientHeight;
                    const sx = pos.x * (cw / 800) * zoomLevel + panOffsetX;
                    const sy = pos.y * (ch / 600) * zoomLevel + panOffsetY;
                    const floatLeft = Math.max(4, Math.min(sx - 90, cw - 188));
                    const floatTop = Math.max(4, Math.min(sy - 215, ch - 220));
                    const zoneColor = ZONE_CONFIG[mol.zone]?.color || '#94a3b8';
                    return (
                      <line
                        key={`line-${mol.id}`}
                        x1={sx} y1={sy}
                        x2={floatLeft + 90} y2={floatTop + 185}
                        stroke={zoneColor}
                        strokeWidth="1.5"
                        strokeOpacity="0.5"
                      />
                    );
                  })}
                </svg>
              )}

              {/* Floating structure images — pinned on scatter plot */}
              {animationPhase === 'complete' && Array.from(pinnedMolecules.values()).map(mol => {
                const pos = positions[`mol-${mol.id}`];
                if (!pos || !containerRef.current) return null;
                const cw = containerRef.current.clientWidth;
                const ch = containerRef.current.clientHeight;
                const sx = pos.x * (cw / 800) * zoomLevel + panOffsetX;
                const sy = pos.y * (ch / 600) * zoomLevel + panOffsetY;
                const isActive = selectedMolecule?.id === mol.id;
                const isDark = isDarkMode;
                return (
                  <div
                    key={`float-${mol.id}`}
                    className="group"
                    style={{
                      position: 'absolute',
                      left: Math.max(4, Math.min(sx - 90, cw - 188)),
                      top: Math.max(4, Math.min(sy - 215, ch - 220)),
                      width: 180,
                      zIndex: isActive ? 25 : 15,
                      pointerEvents: 'auto',
                      cursor: 'pointer',
                    }}
                    onClick={(e) => { e.stopPropagation(); setSelectedMolecule(mol); }}
                  >
                    {/* Close button — visible on hover */}
                    <button
                      onClick={(e) => { e.stopPropagation(); togglePin(mol); }}
                      className="opacity-0 group-hover:opacity-100 transition-opacity"
                      style={{
                        position: 'absolute', top: -6, right: -6, width: 18, height: 18,
                        borderRadius: '50%', border: 'none', cursor: 'pointer',
                        backgroundColor: isDark ? 'rgba(30,41,59,0.85)' : 'rgba(100,116,139,0.8)',
                        color: '#e2e8f0', fontSize: 12, fontWeight: 700,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        zIndex: 5,
                      }}
                    >{'\u00D7'}</button>
                    <div style={{
                      filter: isActive ? 'drop-shadow(0 0 8px rgba(20,184,166,0.7))' : 'drop-shadow(0 1px 4px rgba(0,0,0,0.5))',
                    }}>
                      <StructureImage smiles={mol.smiles} size={180} />
                    </div>
                    <div style={{
                      fontSize: 11, textAlign: 'center', marginTop: 2,
                      color: isDark ? '#e2e8f0' : '#1e293b',
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                      textShadow: isDark ? '0 1px 3px rgba(0,0,0,0.8)' : '0 1px 2px rgba(255,255,255,0.8)',
                    }}>
                      {mol.name || `Mol ${mol.id}`}
                    </div>
                  </div>
                );
              })}

            </div>

        ) : null}

        {/* Shared detail panel — absolute overlay on right, shown for scatter & globe */}
        {(viewMode === 'scatter' || viewMode === 'globe') && (selectedMolecule || selectedPatent) && (
          <div
            className="w-[340px] overflow-y-auto"
            style={{ position: 'absolute', right: 0, top: 0, bottom: 0, zIndex: 40, backgroundColor: 'var(--card-bg)', borderLeft: '1px solid var(--card-border)', color: 'var(--text-primary)', boxShadow: '-4px 0 16px rgba(0,0,0,0.15)' }}
          >
            {/* Close button */}
            <button
              onClick={() => { setSelectedMolecule(null); setSelectedPatent(null); }}
              className="absolute top-3 right-3 z-10 w-7 h-7 rounded-full flex items-center justify-center transition-colors"
              style={{ backgroundColor: 'var(--hover-bg)', color: 'var(--text-muted)' }}
              title="Close"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            {selectedPatent ? (
              <PatentDetails patent={selectedPatent} molecules={mapData?.molecules || []} />
            ) : selectedMolecule ? (
              <div className="p-4">
                {/* Structure image — large */}
                <div className="flex justify-center mb-4">
                  <StructureImage smiles={selectedMolecule.smiles} size={200} />
                </div>
                {/* Name + ID */}
                <h3 className="text-base font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>
                  {selectedMolecule.name || `Molecule ${selectedMolecule.id}`}
                </h3>
                <div className="text-xs mb-3" style={{ color: 'var(--text-muted)' }}>ID: {selectedMolecule.id}</div>
                {/* SMILES with copy */}
                {selectedMolecule.smiles && (
                  <div className="mb-3">
                    <div className="text-xs font-semibold uppercase tracking-wide mb-1" style={{ color: 'var(--text-secondary)' }}>SMILES</div>
                    <div className="flex items-start gap-2">
                      <div className="text-[11px] font-mono p-2 rounded break-all flex-1 max-h-16 overflow-auto" style={{ backgroundColor: 'var(--content-bg)', color: 'var(--text-muted)' }}>
                        {selectedMolecule.smiles}
                      </div>
                      <button
                        onClick={() => navigator.clipboard.writeText(selectedMolecule.smiles)}
                        className="px-2 py-1 text-[10px] rounded flex-shrink-0 transition-colors"
                        style={{ backgroundColor: 'var(--hover-bg)', color: 'var(--text-secondary)' }}
                        title="Copy SMILES"
                      >Copy</button>
                    </div>
                  </div>
                )}
                {/* Zone badge + risk */}
                {(() => {
                  const zone = selectedMolecule.zone || 5;
                  const zc = ZONE_CONFIG[zone];
                  return (
                    <div className="mb-3">
                      <span className="inline-block px-2.5 py-1 rounded text-xs font-semibold text-white" style={{ backgroundColor: zc.color }}>
                        Zone {zone}: {zc.label}
                      </span>
                      <div className="text-xs mt-1 italic" style={{ color: 'var(--text-secondary)' }}>{zc.risk}</div>
                      <div className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>{zc.description}</div>
                    </div>
                  );
                })()}
                {/* Patent match stats */}
                <div className="grid grid-cols-2 gap-2 mb-3">
                  <div className="rounded-lg p-2.5" style={{ backgroundColor: 'var(--content-bg)' }}>
                    <div className="text-xs mb-0.5" style={{ color: 'var(--text-muted)' }}>Exact Matches</div>
                    <div className="text-lg font-semibold" style={{ color: '#ef4444' }}>{selectedMolecule.patent_count_exact || 0}</div>
                  </div>
                  <div className="rounded-lg p-2.5" style={{ backgroundColor: 'var(--content-bg)' }}>
                    <div className="text-xs mb-0.5" style={{ color: 'var(--text-muted)' }}>Similar Matches</div>
                    <div className="text-lg font-semibold" style={{ color: '#eab308' }}>{selectedMolecule.patent_count_similar || 0}</div>
                  </div>
                </div>
                {selectedMolecule.max_similarity != null && (
                  <div className="rounded-lg p-2.5 mb-3" style={{ backgroundColor: 'var(--content-bg)' }}>
                    <div className="text-xs mb-0.5" style={{ color: 'var(--text-muted)' }}>Max Similarity</div>
                    <div className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>{(selectedMolecule.max_similarity * 100).toFixed(1)}%</div>
                  </div>
                )}
                {/* Patent matches list */}
                {(() => {
                  const matches = selectedMolecule.top_patent_matches || selectedMolecule.patent_matches || [];
                  return matches.length > 0 ? (
                    <div className="mt-3 pt-3" style={{ borderTop: '1px solid var(--card-border)' }}>
                      <h4 className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: 'var(--text-secondary)' }}>
                        Patent Matches ({matches.length})
                      </h4>
                      <div className="space-y-1.5 max-h-48 overflow-y-auto">
                        {matches.slice(0, 10).map((pm: any, i: number) => {
                          const sId = pm.surechembl_id || pm.patent_number || 'Unknown';
                          const sim = pm.similarity ? (pm.similarity * 100).toFixed(1) : null;
                          return (
                            <div key={i} className="flex items-center gap-2 text-xs">
                              {sim && (
                                <span className={`px-1.5 py-0.5 rounded font-semibold text-white ${
                                  pm.similarity >= 0.95 ? 'bg-red-500' : pm.similarity >= 0.80 ? 'bg-amber-500' : 'bg-green-500'
                                }`}>{sim}%</span>
                              )}
                              <span
                                className="truncate"
                                style={{ color: 'var(--text-primary)' }}
                              >{sId}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : null;
                })()}
                {/* Full Detail button */}
                <button
                  onClick={() => onMoleculeSelect?.(selectedMolecule)}
                  className="w-full mt-4 px-3 py-2 text-xs font-medium bg-teal-600 hover:bg-teal-500 text-white rounded-lg transition-colors"
                >
                  Full Detail
                </button>
                {/* Pinned count + clear */}
                {pinnedMolecules.size > 0 && (
                  <div className="flex items-center justify-between mt-4 pt-3" style={{ borderTop: '1px solid var(--card-border)' }}>
                    <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>{pinnedMolecules.size} pinned on map</span>
                    <button
                      onClick={() => { setPinnedMolecules(new Map()); setSelectedMolecule(null); }}
                      className="text-xs px-2 py-1 rounded transition-colors"
                      style={{ color: 'var(--text-muted)', backgroundColor: 'var(--hover-bg)' }}
                    >Clear all</button>
                  </div>
                )}
              </div>
            ) : null}
          </div>
        )}

      </div>

      {/* Not enriched warning */}
      {enrichedCount === 0 && totalMolecules > 0 && (
        <div className="px-5 py-3 bg-yellow-50 dark:bg-yellow-900/20 border-t text-yellow-700 dark:text-yellow-500 text-sm text-center" style={{ borderColor: 'var(--card-border)' }}>
          This file hasn't been enriched. Use "Enrich with Patents" to scan for patent conflicts.
        </div>
      )}

      {/* Compound detail modal — replaces direct SureChEMBL links */}
      <CompoundDetailModal
        isOpen={!!compoundDetail}
        onClose={() => setCompoundDetail(null)}
        surechemblId={compoundDetail?.id || ''}
        smiles={compoundDetail?.smiles}
        similarity={compoundDetail?.similarity}
        zone={compoundDetail?.zone}
        patentNumber={compoundDetail?.patent}
      />

      {/* External link warning */}
      <ExternalLinkWarning
        url={extWarning.url}
        isOpen={extWarning.isOpen}
        onClose={() => setExtWarning(w => ({ ...w, isOpen: false }))}
        onConfirm={() => setExtWarning(w => ({ ...w, isOpen: false }))}
      />
    </div>
  );
};

// StructureImage imported from shared component above

// Molecule details panel
const MoleculeDetails: React.FC<{ molecule: MoleculeData }> = ({ molecule }) => {
  const zone = molecule.zone || 5;
  const zoneConfig = ZONE_CONFIG[zone];
  const [mdCompound, setMdCompound] = useState<{ id: string; smiles?: string; similarity?: number; zone?: number; patent?: string } | null>(null);
  const [mdPatent, setMdPatent] = useState<string | null>(null);
  const [mdExtWarning, setMdExtWarning] = useState({ url: '', isOpen: false });

  return (
    <div className="p-5">
      <h4 className="text-sm font-semibold text-slate-100 mb-4 pb-3 border-b border-slate-700">Molecule Details</h4>

      {molecule.smiles && (
        <div className="mb-4 p-3 bg-slate-900 rounded-lg">
          <StructureImage smiles={molecule.smiles} size={160} />
        </div>
      )}

      <div className="mb-3">
        <div className="text-xs text-slate-500 uppercase tracking-wide mb-1">Name</div>
        <div className="text-sm text-slate-200">{molecule.name || `ID: ${molecule.id}`}</div>
      </div>

      <div className="mb-3">
        <div className="text-xs text-slate-500 uppercase tracking-wide mb-1">SMILES</div>
        <div className="text-xs font-mono bg-slate-900 p-2 rounded text-slate-400 break-all max-h-20 overflow-auto">
          {molecule.smiles}
        </div>
      </div>

      <div className="mb-4">
        <div className="text-xs text-slate-500 uppercase tracking-wide mb-1">Collision Zone</div>
        <span
          className="inline-block px-3 py-1 rounded text-xs font-semibold text-white"
          style={{ backgroundColor: zoneConfig.color }}
        >
          Zone {zone}: {zoneConfig.label}
        </span>
        <div className="text-xs text-slate-400 mt-1 italic">{zoneConfig.risk}</div>
      </div>

      {/* Isostere detection details for Zone 2 and 3 */}
      {(zone === 2 || zone === 3) && (
        <div className="mb-4 p-3 rounded-lg" style={{ backgroundColor: `${zoneConfig.color}20`, border: `1px solid ${zoneConfig.color}40` }}>
          <div className="text-xs text-slate-400 uppercase tracking-wide mb-2">
            {zone === 2 ? 'Chemical' : 'Biological'} Isostere Detected
          </div>
          {molecule.isostere_name ? (
            <>
              <div className="text-sm font-medium text-slate-100 mb-1">
                {molecule.isostere_name?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
              </div>
              {molecule.transform_smirks && (
                <div className="text-xs font-mono text-slate-400 bg-slate-900/50 p-2 rounded mt-2">
                  {molecule.transform_smirks}
                </div>
              )}
            </>
          ) : (
            <div className="text-sm font-medium text-slate-100 mb-1">
              {zone === 2 ? (
                (() => {
                  const matches = molecule.top_patent_matches || molecule.patent_matches || [];
                  const bestSim = matches.length > 0 ? Math.max(...matches.map(m => m.similarity || 0)) : 0;
                  if (bestSim >= 0.95) return 'F → Cl substitution';
                  if (bestSim >= 0.90) return 'CH₃ → CF₃ exchange';
                  if (bestSim >= 0.85) return 'Halogen exchange (F/Cl/Br)';
                  return 'Halogen or methyl substitution';
                })()
              ) : (
                (() => {
                  const matches = molecule.top_patent_matches || molecule.patent_matches || [];
                  const bestSim = matches.length > 0 ? Math.max(...matches.map(m => m.similarity || 0)) : 0;
                  if (bestSim >= 0.85) return 'COOH → Tetrazole bioisostere';
                  if (bestSim >= 0.80) return 'Phenyl → Pyridyl replacement';
                  if (bestSim >= 0.75) return 'Amide → Ester bioisostere';
                  return 'Functional group bioisostere';
                })()
              )}
            </div>
          )}
          {(() => {
            const matches = molecule.top_patent_matches || molecule.patent_matches || [];
            const bestSim = matches.length > 0 ? Math.max(...matches.map(m => m.similarity || 0)) : 0;
            return bestSim > 0 && (
              <div className="text-xs text-slate-400 mt-2">
                Similarity: {(bestSim * 100).toFixed(1)}%
              </div>
            );
          })()}
          {molecule.matched_smiles && (
            <div className="mt-2">
              <div className="text-xs text-slate-500 mb-1">Matched Patent Structure</div>
              <div className="text-xs font-mono text-slate-400 break-all">{molecule.matched_smiles}</div>
            </div>
          )}
        </div>
      )}

      {/* MCS details for Zone 4 */}
      {zone === 4 && molecule.mcs_fraction && (
        <div className="mb-4 p-3 rounded-lg" style={{ backgroundColor: `${zoneConfig.color}20`, border: `1px solid ${zoneConfig.color}40` }}>
          <div className="text-xs text-slate-400 uppercase tracking-wide mb-2">Maximum Common Substructure</div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <div className="text-xs text-slate-500">MCS Atoms</div>
              <div className="text-sm font-semibold text-slate-100">{molecule.mcs_atoms || '—'}</div>
            </div>
            <div>
              <div className="text-xs text-slate-500">MCS Fraction</div>
              <div className="text-sm font-semibold text-slate-100">{((molecule.mcs_fraction || 0) * 100).toFixed(1)}%</div>
            </div>
          </div>
        </div>
      )}

      {/* Zone 1 literal match */}
      {zone === 1 && (
        <div className="mb-4 p-3 rounded-lg bg-red-900/20 border border-red-900/40">
          <div className="text-xs text-red-400 uppercase tracking-wide mb-1">Found in Patent Literature</div>
          <div className="text-sm text-slate-200">
            Exact match to a structure in the SureChEMBL patent compound database (28.4M compounds).
          </div>
        </div>
      )}

      {/* Zone 5 novel */}
      {zone === 5 && molecule.enriched_at && (
        <div className="mb-4 p-3 rounded-lg bg-green-900/20 border border-green-900/40">
          <div className="text-xs text-green-400 uppercase tracking-wide mb-1">Freedom to Operate</div>
          <div className="text-sm text-slate-200">
            No collision detected with known patent claims.
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="bg-slate-900 rounded-lg p-3">
          <div className="text-xs text-slate-500 mb-1">Literal Matches</div>
          <div className="text-lg font-semibold text-red-400">{molecule.patent_count_exact}</div>
        </div>
        <div className="bg-slate-900 rounded-lg p-3">
          <div className="text-xs text-slate-500 mb-1">Isostere/Analog</div>
          <div className="text-lg font-semibold text-yellow-400">{molecule.patent_count_similar}</div>
        </div>
      </div>

      {(() => {
        const matches = molecule.top_patent_matches || molecule.patent_matches || [];
        return matches.length > 0 && (
          <div className="mt-5 pt-4 border-t border-slate-700">
            <h5 className="text-xs font-semibold text-slate-400 mb-3">
              Patent Matches ({matches.length})
            </h5>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {matches.slice(0, 10).map((pm, i) => {
                const sId = pm.surechembl_id || pm.patent_number || 'Unknown';
                const sim = pm.similarity ? (pm.similarity * 100).toFixed(1) : null;
                const isExact = pm.match_type === 'exact' || (pm.similarity && pm.similarity >= 0.99);
                return (
                  <div key={i} className="bg-slate-900 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-1">
                      {sim && (
                        <span
                          className={`px-2 py-0.5 rounded text-xs font-semibold text-white ${
                            pm.similarity! >= 0.9 ? 'bg-red-600' : 'bg-yellow-600'
                          }`}
                        >
                          Tanimoto {sim}%
                        </span>
                      )}
                      <span className="text-[10px] text-slate-500">
                        {isExact ? 'exact match' : 'similar'}
                      </span>
                    </div>
                    <div className="text-xs mb-1">
                      <button
                        onClick={() => setMdCompound({
                          id: sId,
                          smiles: pm.matched_smiles || undefined,
                          similarity: pm.similarity ?? undefined,
                          patent: pm.patent_number || undefined,
                        })}
                        className="font-medium text-blue-400 hover:text-blue-300 underline"
                        style={{ background: 'none', border: 'none', padding: 0, font: 'inherit', cursor: 'pointer' }}
                      >
                        {sId}
                      </button>
                      {pm.patent_number && pm.patent_number !== sId && (
                        <button
                          onClick={() => setMdPatent(pm.patent_number!)}
                          className="text-slate-400 hover:text-blue-300 ml-2 underline"
                          style={{ background: 'none', border: 'none', padding: 0, font: 'inherit', cursor: 'pointer' }}
                        >
                          {pm.patent_number}
                        </button>
                      )}
                    </div>
                    {pm.matched_smiles && (
                      <div className="text-xs font-mono text-slate-500 truncate">{pm.matched_smiles}</div>
                    )}
                  </div>
                );
              })}
              {matches.length > 10 && (
                <div className="text-xs text-slate-500 text-center py-2">
                  +{matches.length - 10} more patents
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {(molecule.top_patent_matches || molecule.patent_matches || []).length === 0 && molecule.zone === 5 && (
        <div className="flex items-center gap-2 p-4 bg-green-900/20 rounded-lg text-green-500 text-sm font-medium mt-4">
          <span className="text-lg">✓</span>
          No patent conflicts found
        </div>
      )}

      {!molecule.enriched_at && (
        <div className="flex items-center gap-2 p-4 bg-slate-700/50 rounded-lg text-slate-400 text-sm mt-4">
          <span className="text-lg">○</span>
          Not yet enriched
        </div>
      )}

      <CompoundDetailModal
        isOpen={!!mdCompound}
        onClose={() => setMdCompound(null)}
        surechemblId={mdCompound?.id || ''}
        smiles={mdCompound?.smiles}
        similarity={mdCompound?.similarity}
        zone={mdCompound?.zone}
        patentNumber={mdCompound?.patent}
      />
      <PatentDetailModal
        isOpen={!!mdPatent}
        onClose={() => setMdPatent(null)}
        patentNumber={mdPatent || ''}
      />
      <ExternalLinkWarning
        url={mdExtWarning.url}
        isOpen={mdExtWarning.isOpen}
        onClose={() => setMdExtWarning(w => ({ ...w, isOpen: false }))}
        onConfirm={() => setMdExtWarning(w => ({ ...w, isOpen: false }))}
      />
    </div>
  );
};

// Patent details panel
const PatentDetails: React.FC<{ patent: PatentSummary; molecules: MoleculeData[] }> = ({ patent, molecules }) => {
  const [pdExtWarning, setPdExtWarning] = useState({ url: '', isOpen: false });

  // Find molecules that match this patent (support both API formats)
  const matchingMolecules = molecules.filter(m => {
    const matches = m.top_patent_matches || m.patent_matches || [];
    return matches.some(pm =>
      (pm.surechembl_id === patent.patent_number) ||
      (pm.patent_number === patent.patent_number)
    );
  });

  return (
    <div className="p-5">
      <h4 className="text-sm font-bold mb-4 pb-3" style={{ color: 'var(--text-primary)', borderBottom: '1px solid var(--card-border)' }}>Patent Details</h4>

      <div className="mb-3">
        <div className="text-xs uppercase tracking-wide mb-1 font-medium" style={{ color: 'var(--text-secondary)' }}>Patent Number</div>
        <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
          {patent.patent_number}
        </span>
      </div>

      {patent.patent_title && (
        <div className="mb-3">
          <div className="text-xs uppercase tracking-wide mb-1 font-medium" style={{ color: 'var(--text-secondary)' }}>Title</div>
          <div className="text-sm leading-relaxed" style={{ color: 'var(--text-primary)' }}>{patent.patent_title}</div>
        </div>
      )}

      <div className="grid grid-cols-3 gap-2 mb-4">
        <div className="rounded-lg p-3 text-center" style={{ backgroundColor: 'var(--content-bg)' }}>
          <div className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>{patent.match_count}</div>
          <div className="text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>Total</div>
        </div>
        <div className="rounded-lg p-3 text-center" style={{ backgroundColor: 'var(--content-bg)' }}>
          <div className="text-lg font-bold" style={{ color: '#ef4444' }}>{patent.exact_count}</div>
          <div className="text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>Exact</div>
        </div>
        <div className="rounded-lg p-3 text-center" style={{ backgroundColor: 'var(--content-bg)' }}>
          <div className="text-lg font-bold" style={{ color: '#eab308' }}>{patent.similar_count}</div>
          <div className="text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>Similar</div>
        </div>
      </div>

      {/* External link buttons */}
      <div className="flex gap-2 mb-4">
        <button
          onClick={() => openExternalLink(`https://patents.google.com/patent/${patent.patent_number}`, setPdExtWarning)}
          className="flex-1 px-3 py-2 text-xs font-medium text-white rounded-lg transition-colors"
          style={{ backgroundColor: '#0d9488' }}
          onMouseEnter={(e) => { (e.target as HTMLElement).style.backgroundColor = '#0f766e'; }}
          onMouseLeave={(e) => { (e.target as HTMLElement).style.backgroundColor = '#0d9488'; }}
        >
          Google Patents &#8599;
        </button>
        <button
          onClick={() => openExternalLink(`https://worldwide.espacenet.com/patent/search?q=${patent.patent_number}`, setPdExtWarning)}
          className="flex-1 px-3 py-2 text-xs font-medium rounded-lg transition-colors"
          style={{ color: '#0d9488', border: '1px solid #0d9488', backgroundColor: 'transparent' }}
          onMouseEnter={(e) => { (e.target as HTMLElement).style.backgroundColor = 'rgba(13,148,136,0.1)'; }}
          onMouseLeave={(e) => { (e.target as HTMLElement).style.backgroundColor = 'transparent'; }}
        >
          Espacenet &#8599;
        </button>
      </div>

      {matchingMolecules.length > 0 && (
        <div className="mt-5 pt-4" style={{ borderTop: '1px solid var(--card-border)' }}>
          <h5 className="text-xs font-semibold mb-3" style={{ color: 'var(--text-secondary)' }}>
            Affected Molecules ({matchingMolecules.length})
          </h5>
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {matchingMolecules.slice(0, 8).map((mol) => {
              const matches = mol.top_patent_matches || mol.patent_matches || [];
              const match = matches.find(pm =>
                (pm.surechembl_id === patent.patent_number) ||
                (pm.patent_number === patent.patent_number)
              );
              const similarity = match?.similarity || 0;
              return (
                <div key={mol.id} className="rounded-lg p-3" style={{ backgroundColor: 'var(--content-bg)' }}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-medium" style={{ color: 'var(--text-primary)' }}>
                      {mol.name || `Molecule ${mol.id}`}
                    </span>
                    <span
                      className={`px-2 py-0.5 rounded text-xs font-semibold text-white ${
                        similarity >= 0.9 ? 'bg-red-600' : 'bg-yellow-600'
                      }`}
                    >
                      {(similarity * 100).toFixed(0)}%
                    </span>
                  </div>
                  <div style={{ marginTop: 4 }}><StructureImage smiles={mol.smiles} size={80} /></div>
                </div>
              );
            })}
            {matchingMolecules.length > 8 && (
              <div className="text-xs text-center py-2" style={{ color: 'var(--text-secondary)' }}>
                +{matchingMolecules.length - 8} more molecules
              </div>
            )}
          </div>
        </div>
      )}

      <ExternalLinkWarning
        url={pdExtWarning.url}
        isOpen={pdExtWarning.isOpen}
        onClose={() => setPdExtWarning(w => ({ ...w, isOpen: false }))}
        onConfirm={() => setPdExtWarning(w => ({ ...w, isOpen: false }))}
      />
    </div>
  );
};

export default PatentCollisionMap;
